import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { formatDate, formatDateTime } from "../lib/date";
import { TOKEN_KEY, isTokenExpired, applyAuthToken } from "../lib/authToken";
import { toast } from "sonner";
import TriagePanel from "../components/dashboard/triage/TriagePanel";
import AtlassianChip from "../components/dashboard/triage/AtlassianChip";
import {
  Ticket,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  Trophy,
  Target,
  CheckCircle,
  Circle,
  Plus,
  ExternalLink,
  LogOut,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  X,
  MessageSquare,
  Send,
  Sparkles,
  Loader2,
  FileAudio,
  BookOpen,
  Lightbulb,
  Zap,
  Heart,
  Star,
  Coffee,
  Bell,
  CheckCheck,
  MessageCircle,
  XCircle,
  Users,
  ArrowRight,
  GitBranch,
  LayoutDashboard,
  ClipboardCheck,
  GraduationCap,
  TrendingDown,
  ThumbsUp,
  ThumbsDown,
  UserPlus,
  Inbox,
  Wand2,
  Copy,
  ChevronDown,
  ArrowUpRight,
  Crown,
  FileCheck,
  FileText,
  Code2,
  ChevronUp,
  Play,
  ChevronsDown,
  RotateCcw,
  Moon,
  Sun,
  Paperclip,
  Image as ImageIcon,
  Eye,
  Search,
  Filter,
  Building2,
  Mail,
  Maximize2,
  Minimize2,
  Ban
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { QueueRow, IdleTag } from "../components/dashboard/QueueRow";
import { AgentQueueTable } from "../components/dashboard/AgentQueueTable";
import { RunbookModal } from "../components/dashboard/RunbookModal";
import { AskTheCodeDrawer } from "../components/dashboard/AskTheCodeDrawer";
import { QueueHealthGauge } from "../components/QueueHealthGauge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const JIRA_URL = "https://equiprsoftware.atlassian.net/browse";

// ─── Queue Health Meter ─────────────────────────────────────────────
// Replaces gamification "points". Health is driven by how long the agent's
// "Waiting for customer" / "Waiting for support" tickets have been sitting.
// Long-waiting tickets should be chased or closed (Resolved Without Email).
const BUSINESS_DAY_HOURS = 9.5; // 07:30–17:00 AEST support day

function computeQueueHealth(tickets = []) {
  const waiting = tickets.filter((t) => (t.status || "").toLowerCase().includes("waiting for"));
  let problem = 0;
  const breakdown = [];

  waiting.forEach((t) => {
    const status = t.status || "";
    const sLower = status.toLowerCase();
    const bd = (t.idle_hours || 0) / BUSINESS_DAY_HOURS;
    const onSupport = sLower.includes("support");
    let severity = "ok";
    let recommendation = "";

    if (onSupport) {
      // Ticket is in OUR court — the customer is waiting on us
      if (bd >= 2) { severity = "critical"; recommendation = "Customer has been waiting on us for 2+ days — respond or progress now."; }
      else if (bd >= 1) { severity = "attention"; recommendation = "Customer is waiting on us — reply today."; }
    } else {
      // Waiting for customer — chase or close if stale
      if (bd >= 3) { severity = "critical"; recommendation = "No response in 3+ days — consider closing as Resolved Without Email."; }
      else if (bd >= 2) { severity = "attention"; recommendation = "Send a gentle chase / follow-up."; }
    }

    if (severity === "critical") problem += 1;
    else if (severity === "attention") problem += 0.5;

    if (severity !== "ok") {
      breakdown.push({ key: t.key, summary: t.summary, status, business_days: Math.floor(bd), severity, recommendation });
    }
  });

  const total = waiting.length;
  const score = total > 0 ? Math.max(0, Math.round(100 * (1 - problem / total))) : 100;
  let label, color;
  if (score >= 80) { label = "Healthy"; color = "#0E6B3F"; }
  else if (score >= 50) { label = "Attention"; color = "#6B5600"; }
  else { label = "At Risk"; color = "#C23A00"; }

  const order = { critical: 0, attention: 1 };
  breakdown.sort((a, b) => (order[a.severity] - order[b.severity]) || (b.business_days - a.business_days));

  const wfs = waiting.filter((t) => (t.status || "").toLowerCase().includes("support")).length;
  return {
    score, label, color,
    total_waiting: total,
    waiting_for_support: wfs,
    waiting_for_customer: total - wfs,
    needs_action: breakdown.length,
    breakdown,
  };
}

// Idle magnitude ladder (§ item 5): tone the wait by how long, not one threshold
const idleTone = (h) => {
  if (h === null || h === undefined) return { ink: "#8A857A", dot: "#8A857A" };
  if (h > 24) return { ink: "#9A3200", dot: "#C23A00" };
  if (h >= 8) return { ink: "#6B5600", dot: "#E0B800" };
  return { ink: "#1A1A1A", dot: "#8A857A" };
};

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [myCalls, setMyCalls] = useState([]);
  const [myScore, setMyScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Queue Health Meter (replaces gamification points)
  const [showQueueHealth, setShowQueueHealth] = useState(false);
  const queueHealth = useMemo(() => computeQueueHealth(myTickets), [myTickets]);
  // My Workload card collapse (persisted; defaults to rolled-up to save space)
  const [workloadCollapsed, setWorkloadCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("agent_workload_collapsed");
      return saved === null ? true : saved === "true";
    } catch (e) {
      return true;
    }
  });
  const toggleWorkloadCollapsed = () => {
    setWorkloadCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("agent_workload_collapsed", String(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };
  // My Workload overview (mirrors the manager Agent Workload card, for the agent's own view)
  const myWorkload = useMemo(() => {
    const tix = (myTickets || []).filter((t) => {
      const s = (t.status || "").toLowerCase();
      return !s.includes("resolved") && !s.includes("closed") && !s.includes("done");
    });
    const high = tix.filter((t) => t.priority === "Highest" || t.priority === "High").length;
    const slaRisk = tix.filter((t) => t.sla_risk || t.sla_breached).length;
    const avgIdle = tix.length ? Math.round((tix.reduce((s, t) => s + (t.idle_hours || 0), 0) / tix.length) * 10) / 10 : 0;
    const statusBreakdown = {};
    tix.forEach((t) => { const st = t.status || "Unknown"; statusBreakdown[st] = (statusBreakdown[st] || 0) + 1; });
    const clientCounts = {};
    tix.forEach((t) => { const org = (t.organization || "").trim() || "No organisation"; clientCounts[org] = (clientCounts[org] || 0) + 1; });
    const clients = Object.entries(clientCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return { open: tix.length, high, slaRisk, avgIdle, statusBreakdown, clients };
  }, [myTickets]);
  
  // Ticket creation state
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    product: "Allocate + Operator",
    requestTypeId: "",
    summary: "",
    description: "",
    priority: "3", // Medium
    organizationId: "",
    reporterEmail: ""
  });
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationContacts, setOrganizationContacts] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [requestTypes, setRequestTypes] = useState([]);
  const [ticketAttachments, setTicketAttachments] = useState([]); // File[]

  // My Tickets search/filter state
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("all");
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState("all");
  const [refreshingTickets, setRefreshingTickets] = useState(false);
  const [vipFilterActive, setVipFilterActive] = useState(false);
  const [aiSuggestionFilter, setAiSuggestionFilter] = useState("all"); // "all", "with", "without"
  const [ticketsFullscreen, setTicketsFullscreen] = useState(false);

  // Per-agent queue: split tickets into New / Active and allow "send to bottom"
  const [queueState, setQueueState] = useState({}); // key -> {acknowledged_at, queue_position}
  const [queueTab, setQueueTab] = useState("active"); // "active" | "snoozed" | "escalated" (New is shown in its own panel above)
  // Product is a tab (directive §2); persists per browser. Source (JSD/EQ) is plumbing.
  const [productTab, setProductTab] = useState(() => localStorage.getItem("equipr_agent_product") || "all");
  // Light / warm-dark theme toggle scoped to the Agent View (directive §7)
  const [agentTheme, setAgentTheme] = useState(() => localStorage.getItem("equipr_agent_theme") || "light");
  // Client runbook opens in a modal (not the full-screen page)
  const [runbookCompany, setRunbookCompany] = useState(null);
  useEffect(() => { localStorage.setItem("equipr_agent_product", productTab); }, [productTab]);
  useEffect(() => { localStorage.setItem("equipr_agent_theme", agentTheme); }, [agentTheme]);
  const [selectedNewKeys, setSelectedNewKeys] = useState(new Set());
  const [selectedActiveKeys, setSelectedActiveKeys] = useState(new Set());
  const [bulkAcking, setBulkAcking] = useState(false);
  const [bulkMarkingNew, setBulkMarkingNew] = useState(false);

  // Tickets the current agent has escalated (server-side authoritative list
  // from `escalations` collection — survives status changes and resolution).
  const [myEscalations, setMyEscalations] = useState([]);
  const [escalationsLoading, setEscalationsLoading] = useState(false);

  // Team-lead state: top-level view toggle and the consolidated overview
  // payload from /api/team/overview. Only fetched/shown for `role === "team_lead"`.
  const [pageView, setPageView] = useState("my_tickets"); // "my_tickets" | "my_team"
  const [teamOverview, setTeamOverview] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);

  // AI Suggestion Ignore state
  const [showIgnoreDialog, setShowIgnoreDialog] = useState(false);
  // Generic confirm-before-consequence dialog (§ directive item 11)
  const [confirmAction, setConfirmAction] = useState(null); // { title, description, confirmLabel, onConfirm }
  const [ignoringSuggestion, setIgnoringSuggestion] = useState(null);
  const [ignoreCategory, setIgnoreCategory] = useState("");
  const [ignoreNotes, setIgnoreNotes] = useState("");
  const [submittingIgnore, setSubmittingIgnore] = useState(false);

  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  
  // Smart suggestions state for ticket creation
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionDebounce, setSuggestionDebounce] = useState(null);

  // AI Motivation state
  const [aiMotivation, setAiMotivation] = useState(null);
  const [loadingMotivation, setLoadingMotivation] = useState(false);

  // Stale ticket suggestions state - stored as map by ticket key
  const [ticketSuggestions, setTicketSuggestions] = useState({});
  const [loadingStaleTickets, setLoadingStaleTickets] = useState(false);
  const [staleTicketsStatus, setStaleTicketsStatus] = useState(null); // 'loading', 'refreshing', 'ready', 'error'
  const [aiUsageInfo, setAiUsageInfo] = useState(null); // AI usage limits info

  // Handover tickets state
  const [handoverTickets, setHandoverTickets] = useState([]);
  const [loadingHandovers, setLoadingHandovers] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [handoverSummary, setHandoverSummary] = useState(null);
  const [loadingHandoverSummary, setLoadingHandoverSummary] = useState(false);

  // VIP client lookup state
  const [vipLookup, setVipLookup] = useState({});

  // Learning & Feedback state
  const [agentFeedback, setAgentFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);

  // Knowledge sharing state
  const [showShareKnowledge, setShowShareKnowledge] = useState(false);
  const [shareTicketKey, setShareTicketKey] = useState(null);
  const [shareResolution, setShareResolution] = useState("");
  const [shareSteps, setShareSteps] = useState("");
  const [sharingKnowledge, setSharingKnowledge] = useState(false);

  // Unassigned tickets state
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [assigningTicket, setAssigningTicket] = useState(null);

  // AI Resolution Suggestions state
  const [selectedTicketForSuggestions, setSelectedTicketForSuggestions] = useState(null);

  // Coming back from the Atlassian sign-in: reopen the ticket they were on.
  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get("triage");
    if (key) {
      setSelectedTicketForSuggestions(key);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const [aiResolutionSuggestions, setAiResolutionSuggestions] = useState(null);
  const [loadingResolutionSuggestions, setLoadingResolutionSuggestions] = useState(false);

  // SQL Copilot state
  const [showAskCode, setShowAskCode] = useState(false);
  const [showSqlCopilot, setShowSqlCopilot] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false); // #11 quick-access sidebar
  const [sqlQuestion, setSqlQuestion] = useState("");
  const [sqlContext, setSqlContext] = useState({ docket_number: "", customer: "", booking_id: "" });
  const [sqlResult, setSqlResult] = useState(null);
  const [generatingSql, setGeneratingSql] = useState(false);
  const [sqlClientId, setSqlClientId] = useState("EAG123"); // Default client
  const [availableSchemas, setAvailableSchemas] = useState([]);

  // Start Work state
  const [startingWorkTicket, setStartingWorkTicket] = useState(null);

  // Escalation state
  const [escalatingTicket, setEscalatingTicket] = useState(null);
  const [escalationReason, setEscalationReason] = useState("");
  const [escalationDialogOpen, setEscalationDialogOpen] = useState(false);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [escalationTarget, setEscalationTarget] = useState(""); // For Kevin Carbe's target selection

  // Start work handler
  const handleStartWork = async (ticket) => {
    if (!session) return;
    setStartingWorkTicket(ticket.key);
    try {
      await axios.post(`${API}/tickets/${ticket.key}/start-work`, {
        agent_name: session.name,
        agent_email: session.email
      });
      toast.success(`Started working on ${ticket.key}`);
      // Refresh tickets
      fetchData();
    } catch (error) {
      console.error("Failed to start work:", error);
      toast.error("Failed to record work start");
    } finally {
      setStartingWorkTicket(null);
    }
  };

  // Check if ticket needs escalation (stale + waiting on support)
  const shouldShowEscalateWarning = (ticket) => {
    const status = (ticket.status || "").toLowerCase();
    const isWaitingOnSupport = status.includes("waiting for support") || 
                               status.includes("in progress") ||
                               status === "open";
    const idleHours = ticket.idle_hours || 0;
    // Show escalation warning if waiting on support and idle > 8 hours
    return isWaitingOnSupport && idleHours >= 8;
  };

  // Check if ticket is fresh (recently assigned, not yet started)
  const isFreshTicket = (ticket) => {
    const status = (ticket.status || "").toLowerCase();
    const idleHours = ticket.idle_hours || 0;
    // Fresh = Open/To Do/New status and less than 2 hours idle
    return (status === "open" || status === "to do" || status === "new" || 
            status.includes("waiting for support")) && idleHours < 2;
  };

  // ─── Per-agent queue helpers ────────────────────────────────────────────
  const agentEmail = (session?.email || session?.name || "").toString();

  const isAcknowledged = (key) => {
    // Tab membership is now driven purely by JIRA status:
    //   status == "New"  -> New tab    (isAcknowledged = false)
    //   anything else    -> Active tab (isAcknowledged = true)
    // The acknowledged_at / queue_position state is still kept so agents can
    // re-order Active tickets via "Send to bottom", but it no longer controls
    // which tab a ticket lives in.
    const t = myTickets.find(x => x.key === key);
    if (!t) return false;
    return (t.status || "").toLowerCase() !== "new";
  };

  const ackTicket = async (key, { silent = false } = {}) => {
    if (!key || isAcknowledged(key)) return;
    // Optimistic
    setQueueState(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), acknowledged_at: new Date().toISOString(), queue_position: 0 }
    }));
    try {
      await axios.post(`${API}/agent/queue/acknowledge`, null, {
        params: { email: agentEmail, ticket_key: key }
      });
      if (!silent) toast.success(`${key} moved to Active`);
    } catch (e) {
      console.error("Failed to ack ticket:", e);
    }
  };

  const sendTicketToBottom = async (key) => {
    if (!key) return;
    try {
      const res = await axios.post(`${API}/agent/queue/send-to-bottom`, null, {
        params: { email: agentEmail, ticket_key: key }
      });
      setQueueState(prev => ({
        ...prev,
        [key]: {
          acknowledged_at: prev[key]?.acknowledged_at || new Date().toISOString(),
          queue_position: res.data.queue_position
        }
      }));
      toast.success(`${key} moved to bottom of queue`);
    } catch (e) {
      console.error("Failed to send to bottom:", e);
      toast.error("Failed to update queue");
    }
  };

  const markTicketNew = async (key) => {
    if (!key) return;
    setQueueState(prev => ({
      ...prev,
      [key]: { acknowledged_at: null, queue_position: 0 }
    }));
    try {
      await axios.post(`${API}/agent/queue/mark-new`, null, {
        params: { email: agentEmail, ticket_key: key }
      });
      toast.success(`${key} marked as new`);
    } catch (e) {
      console.error("Failed to mark new:", e);
    }
  };

const snoozeTicket = async (key) => {
    if (!key) return;
    try {
      await axios.post(`${API}/agent/queue/snooze`, null, {
        params: { email: agentEmail, ticket_key: key }
      });
      // Use a far-future sentinel in local state so the ticket is treated as
      // snoozed indefinitely until the agent manually wakes it.
      const indefinite = "2099-12-31T23:59:59+00:00";
      setQueueState(prev => ({
        ...prev,
        [key]: { ...(prev[key] || {}), snoozed_until: indefinite }
      }));
      toast.success(`${key} snoozed`);
    } catch (e) {
      console.error("Failed to snooze:", e);
      toast.error("Failed to snooze ticket");
    }
  };

  const unsnoozeTicket = async (key) => {
    if (!key) return;
    try {
      await axios.post(`${API}/agent/queue/unsnooze`, null, {
        params: { email: agentEmail, ticket_key: key }
      });
      setQueueState(prev => ({
        ...prev,
        [key]: { ...(prev[key] || {}), snoozed_until: null }
      }));
      toast.success(`${key} woken up`);
    } catch (e) {
      console.error("Failed to unsnooze:", e);
      toast.error("Failed to wake ticket");
    }
  };

  /* eslint-disable */
  const isSnoozed = (key) => {
    const s = queueState[key];
    if (!s?.snoozed_until) return false;
    return new Date(s.snoozed_until).getTime() > Date.now();
  };
  /* eslint-enable */

  // ─── Multi-select on New tab ───────────────────────────────────────────
  const toggleSelectKey = (key) => {
    setSelectedNewKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelection = () => setSelectedNewKeys(new Set());

  const selectAllVisibleNew = (visibleKeys) => {
    setSelectedNewKeys(new Set(visibleKeys));
  };

  const bulkMoveSelectedToActive = async () => {
    const keys = Array.from(selectedNewKeys);
    if (keys.length === 0) return;
    setBulkAcking(true);
    // Optimistic local update
    const now = new Date().toISOString();
    setQueueState(prev => {
      const next = { ...prev };
      keys.forEach(k => {
        next[k] = { ...(next[k] || {}), acknowledged_at: now, queue_position: 0 };
      });
      return next;
    });
    try {
      await axios.post(`${API}/agent/queue/bulk-acknowledge`, keys, {
        params: { email: agentEmail }
      });
      toast.success(`Moved ${keys.length} ticket${keys.length === 1 ? "" : "s"} to Active`);
      clearSelection();
    } catch (e) {
      console.error("Bulk ack failed:", e);
      toast.error("Failed to move tickets");
    } finally {
      setBulkAcking(false);
    }
  };

  // ─── Multi-select on Active tab ───────────────────────────────────────
  const toggleSelectActiveKey = (key) => {
    setSelectedActiveKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearActiveSelection = () => setSelectedActiveKeys(new Set());

  const selectAllVisibleActive = (visibleKeys) => {
    setSelectedActiveKeys(new Set(visibleKeys));
  };

  const bulkMoveSelectedToNew = async () => {
    const keys = Array.from(selectedActiveKeys);
    if (keys.length === 0) return;
    setBulkMarkingNew(true);
    // Optimistic local update
    setQueueState(prev => {
      const next = { ...prev };
      keys.forEach(k => {
        next[k] = { acknowledged_at: null, queue_position: 0, snoozed_until: null };
      });
      return next;
    });
    try {
      await Promise.all(keys.map(k =>
        axios.post(`${API}/agent/queue/mark-new`, null, {
          params: { email: agentEmail, ticket_key: k }
        })
      ));
      toast.success(`Moved ${keys.length} ticket${keys.length === 1 ? "" : "s"} back to New`);
      clearActiveSelection();
    } catch (e) {
      console.error("Bulk mark-new failed:", e);
      toast.error("Failed to move tickets");
    } finally {
      setBulkMarkingNew(false);
    }
  };

  // Used by row "Start working" buttons (also auto-called when opening a ticket)
  const handleStartTicket = (key) => ackTicket(key);

  // Map priority -> numeric for sorting (higher = more urgent)
  const priorityWeight = (p) => {
    const v = (p || "").toLowerCase();
    if (v.includes("highest")) return 5;
    if (v.includes("high")) return 4;
    if (v.includes("medium")) return 3;
    if (v.includes("low") && !v.includes("lowest")) return 2;
    if (v.includes("lowest")) return 1;
    return 0;
  };

  // Split tickets into new / active and sort active by priority then queue_position
  const splitTicketsByQueue = (tickets) => {
    const newList = [];
    const activeList = [];
    tickets.forEach(t => {
      if (isAcknowledged(t.key)) activeList.push(t);
      else newList.push(t);
    });
    activeList.sort((a, b) => {
      // Active tab sort: queue_position FIRST. The whole point of "Send to
      // bottom" is to let the agent override priority ordering — if priority
      // wins here, sending a high-priority ticket to the bottom only moves it
      // within its priority bucket, and a refresh visually snaps it back.
      const qa = queueState[a.key]?.queue_position ?? 0;
      const qb = queueState[b.key]?.queue_position ?? 0;
      if (qa !== qb) return qa - qb;
      // Within the same queue_position bucket, fall back to priority then
      // acknowledged_at desc (most recent first).
      const pw = priorityWeight(b.priority) - priorityWeight(a.priority);
      if (pw !== 0) return pw;
      const aa = queueState[a.key]?.acknowledged_at || "";
      const ab = queueState[b.key]?.acknowledged_at || "";
      return ab.localeCompare(aa);
    });
    // New list: keep priority-aware too
    newList.sort((a, b) => priorityWeight(b.priority) - priorityWeight(a.priority));
    return { newList, activeList };
  };

  // Fetch smart suggestions when ticket summary changes
  const fetchSuggestions = async (title, description) => {
    if (!title || title.length < 5) {
      setSuggestions(null);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const response = await axios.post(`${API}/ai/suggestions`, null, {
        params: { title, description: description || "" }
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Debounced suggestion fetch
  const handleTicketTitleChange = (value) => {
    setTicketForm(prev => ({ ...prev, summary: value }));
    
    if (suggestionDebounce) {
      clearTimeout(suggestionDebounce);
    }
    
    const timer = setTimeout(() => {
      fetchSuggestions(value, ticketForm.description);
    }, 500);
    setSuggestionDebounce(timer);
  };

  const askAI = async () => {
    if (!aiQuestion.trim()) return;
    
    setAiLoading(true);
    const question = aiQuestion;
    setAiQuestion("");
    
    // Add question to history
    setAiHistory(prev => [...prev, { type: "user", content: question }]);
    
    try {
      const response = await axios.post(`${API}/ai/ask`, {
        question,
        max_results: 5,
        session_id: session?.id || "default"
      });
      
      setAiResponse(response.data);
      setAiHistory(prev => [...prev, { 
        type: "assistant", 
        content: response.data.answer,
        tickets: response.data.similar_tickets,
        transcripts: response.data.related_transcripts,
        articles: response.data.knowledge_articles
      }]);
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Failed to get AI response");
      setAiHistory(prev => [...prev, { 
        type: "assistant", 
        content: "Sorry, I couldn't process your request. Please try again.",
        error: true
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Fetch AI-generated motivation/tips based on agent's performance
  const fetchAIMotivation = async () => {
    if (!session || !myScore) return;
    
    setLoadingMotivation(true);
    try {
      // First, try to fetch the agent's persona from agent mappings
      let agentPersona = null;
      try {
        const mappingsRes = await axios.get(`${API}/agents/mapping`);
        const agents = mappingsRes.data.agents || [];
        const myMapping = agents.find(a => 
          a.name.toLowerCase() === session.name.toLowerCase() || 
          a.email.toLowerCase() === session.email?.toLowerCase()
        );
        if (myMapping?.persona) {
          agentPersona = myMapping.persona;
        }
      } catch (e) {
        console.log("Could not fetch agent persona:", e);
      }
      
      const response = await axios.post(`${API}/ai/motivation`, {
        agent_name: session.name,
        tickets_resolved: myScore?.tickets_resolved || 0,
        sla_compliance: myScore?.sla_compliance || 100,
        open_tickets: myTickets.filter(t => !t.status?.toLowerCase().includes("resolved")).length,
        points: myScore?.points || 0,
        callbacks_pending: myCalls.filter(c => c.callback_status === "pending" || c.callback_status === "overdue").length,
        persona: agentPersona
      });
      
      setAiMotivation(response.data);
    } catch (error) {
      console.error("Failed to fetch motivation:", error);
      // Set a fallback motivation
      setAiMotivation({
        message: "Keep up the great work! Every ticket you resolve makes a difference.",
        tip: "Remember to document your solutions - it helps the whole team!",
        type: "encouragement"
      });
    } finally {
      setLoadingMotivation(false);
    }
  };

  // Fetch stale ticket suggestions (tickets waiting on customer with no recent response)
  const fetchStaleTicketSuggestions = async (isManualRefresh = false) => {
    if (!session) return;
    
    setLoadingStaleTickets(true);
    if (isManualRefresh) {
      setStaleTicketsStatus('refreshing');
    } else if (Object.keys(ticketSuggestions).length === 0) {
      setStaleTicketsStatus('loading');
    }
    
    try {
      console.log("Fetching stale ticket suggestions for:", session.name);
      const response = await axios.post(`${API}/ai/stale-tickets`, {
        agent_name: session.name
      }, {
        timeout: 120000  // 2 minute timeout for AI analysis
      });
      console.log("Stale ticket suggestions response:", response.data);
      
      // Capture AI usage info
      if (response.data.ai_usage) {
        setAiUsageInfo(response.data.ai_usage);
      }
      
      // Convert array to map by ticket_key for easy lookup
      const suggestionsMap = {};
      (response.data.suggestions || []).forEach(s => {
        suggestionsMap[s.ticket_key] = s;
      });
      
      // Check response status
      const isRefreshing = response.data.refreshing === true;
      const hasSuggestions = Object.keys(suggestionsMap).length > 0;
      
      setTicketSuggestions(suggestionsMap);
      
      if (isRefreshing && !hasSuggestions) {
        // AI is still analyzing - poll again in 15 seconds
        setStaleTicketsStatus('loading');
        setTimeout(() => fetchStaleTicketSuggestions(false), 15000);
      } else if (hasSuggestions) {
        setStaleTicketsStatus('ready');
      } else {
        setStaleTicketsStatus('ready'); // No suggestions to show
      }
    } catch (error) {
      console.error("Failed to fetch stale ticket suggestions:", error);
      setTicketSuggestions({});
      setStaleTicketsStatus('error');
    } finally {
      setLoadingStaleTickets(false);
    }
  };

  // Handle closing a ticket with a follow-up message
  const handleCloseStaleTicket = async (ticket, action) => {
    try {
      if (action === "close") {
        // Add closing comment and transition to resolved
        await axios.post(`${API}/tickets/${ticket.key}/close-stale`, {
          agent_name: session.name,
          message: ticket.suggested_message || "Closing this ticket as the issue appears to be resolved. Please reopen or create a new ticket if you need further assistance."
        });
        toast.success(`${ticket.key} closed successfully`);
      } else if (action === "followup") {
        // Add follow-up comment
        await axios.post(`${API}/tickets/${ticket.key}/followup`, {
          agent_name: session.name,
          message: ticket.followup_message || "Hi, just following up on this ticket. Please let us know if you need any further assistance, otherwise we'll close this ticket in the next few days."
        });
        toast.success(`Follow-up sent for ${ticket.key}`);
      }
      
      // Refresh the suggestions
      fetchStaleTicketSuggestions();
      fetchData();
    } catch (error) {
      console.error("Failed to handle stale ticket:", error);
      toast.error(`Failed to ${action} ticket`);
    }
  };

  // Fetch tickets that were handed over to this agent (worked on by others)
  const fetchHandoverTickets = async () => {
    if (!session) return;
    
    setLoadingHandovers(true);
    try {
      const response = await axios.post(`${API}/ai/handover-tickets`, {
        agent_name: session.name
      });
      setHandoverTickets(response.data.handover_tickets || []);
    } catch (error) {
      console.error("Failed to fetch handover tickets:", error);
      setHandoverTickets([]);
    } finally {
      setLoadingHandovers(false);
    }
  };

  // Fetch AI-generated handover summary for a specific ticket
  const fetchHandoverSummary = async (ticketKey) => {
    setLoadingHandoverSummary(true);
    setSelectedHandover(ticketKey);
    setHandoverSummary(null);
    
    try {
      const response = await axios.get(`${API}/ai/ticket-handover/${ticketKey}`, {
        params: { agent_name: session.name }
      });
      setHandoverSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch handover summary:", error);
      toast.error("Failed to load handover summary");
      setHandoverSummary(null);
    } finally {
      setLoadingHandoverSummary(false);
    }
  };

  // Fetch learning & feedback data for the agent
  const fetchAgentFeedback = async () => {
    if (!session) return;
    
    setLoadingFeedback(true);
    try {
      const response = await axios.post(`${API}/ai/agent-feedback`, {
        agent_name: session.name,
        limit: 10
      });
      setAgentFeedback(response.data);
    } catch (error) {
      console.error("Failed to fetch agent feedback:", error);
      setAgentFeedback(null);
    } finally {
      setLoadingFeedback(false);
    }
  };

  // SQL Copilot functions
  const fetchAvailableSchemas = async () => {
    try {
      const response = await axios.get(`${API}/sql-copilot/schemas`);
      setAvailableSchemas(response.data.schemas || []);
      if (response.data.schemas?.length > 0 && !sqlClientId) {
        setSqlClientId(response.data.schemas[0].client_id);
      }
    } catch (error) {
      console.error("Failed to fetch schemas:", error);
    }
  };

  const generateSqlQuery = async () => {
    if (!sqlQuestion.trim()) {
      toast.error("Please enter a question");
      return;
    }
    
    setGeneratingSql(true);
    setSqlResult(null);
    
    try {
      const response = await axios.post(`${API}/sql-copilot/generate`, {
        question: sqlQuestion,
        client_id: sqlClientId,
        context: {
          docket_number: sqlContext.docket_number || null,
          customer: sqlContext.customer || null,
          booking_id: sqlContext.booking_id || null
        },
        user_name: session?.name
      });
      
      setSqlResult(response.data);
      
      if (response.data.success) {
        toast.success("SQL generated successfully");
      } else {
        toast.error(response.data.error || "Failed to generate SQL");
      }
    } catch (error) {
      console.error("Failed to generate SQL:", error);
      toast.error("Failed to generate SQL query");
    } finally {
      setGeneratingSql(false);
    }
  };

  const copySqlToClipboard = () => {
    if (sqlResult?.sql) {
      navigator.clipboard.writeText(sqlResult.sql);
      toast.success("SQL copied to clipboard");
    }
  };

  const submitSqlFeedback = async (feedback) => {
    if (!sqlResult?.query_id) return;
    
    try {
      await axios.post(`${API}/sql-copilot/feedback`, {
        query_id: sqlResult.query_id,
        feedback: feedback,
        user_name: session?.name
      });
      toast.success("Feedback submitted");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  // Fetch schemas when SQL Copilot opens
  /* eslint-disable */
  useEffect(() => {
    if (showSqlCopilot && availableSchemas.length === 0) {
      fetchAvailableSchemas();
    }
  }, [showSqlCopilot]);
  /* eslint-enable */

  // Share knowledge from a resolved ticket
  const handleShareKnowledge = async () => {
    if (!shareTicketKey || !shareResolution.trim()) {
      toast.error("Please provide a resolution summary");
      return;
    }
    
    setSharingKnowledge(true);
    try {
      await axios.post(`${API}/ai/share-knowledge`, {
        ticket_key: shareTicketKey,
        resolution_summary: shareResolution,
        resolution_steps: shareSteps || null,
        agent_name: session.name,
        tags: []
      });
      toast.success("Knowledge shared successfully! 🎉");
      setShowShareKnowledge(false);
      setShareTicketKey(null);
      setShareResolution("");
      setShareSteps("");
    } catch (error) {
      console.error("Failed to share knowledge:", error);
      toast.error("Failed to share knowledge");
    } finally {
      setSharingKnowledge(false);
    }
  };

  // Fetch unassigned tickets
  const fetchUnassignedTickets = async () => {
    setLoadingUnassigned(true);
    try {
      const response = await axios.get(`${API}/tickets/unassigned?limit=10`);
      setUnassignedTickets(response.data.tickets || []);
    } catch (error) {
      console.error("Failed to fetch unassigned tickets:", error);
      setUnassignedTickets([]);
    } finally {
      setLoadingUnassigned(false);
    }
  };

  // Assign a ticket to current agent
  const handleAssignTicket = async (ticketKey) => {
    setAssigningTicket(ticketKey);
    try {
      await axios.post(`${API}/tickets/${ticketKey}/assign`, {
        agent_name: session.name
      });
      toast.success(`${ticketKey} assigned to you!`);
      // Refresh both lists
      fetchUnassignedTickets();
      fetchData();
    } catch (error) {
      console.error("Failed to assign ticket:", error);
      toast.error(error.response?.data?.detail || "Failed to assign ticket");
    } finally {
      setAssigningTicket(null);
    }
  };

  // Fetch AI resolution suggestions for a ticket
  // Triage now reads Atlassian live inside TriagePanel; this just opens it.
  const fetchResolutionSuggestions = (ticketKey) => setSelectedTicketForSuggestions(ticketKey);

  // Check session on mount
  /* eslint-disable */
  useEffect(() => {
    const storedSession = localStorage.getItem("equipr_session");
    const token = localStorage.getItem(TOKEN_KEY);
    if (!storedSession || !token || isTokenExpired(token)) {
      localStorage.removeItem("equipr_session");
      applyAuthToken(null);
      navigate("/login");
      return;
    }

    const sessionData = JSON.parse(storedSession);

    // Check expiry only if expiresAt is set
    if (sessionData.expiresAt) {
      const expiresAt = new Date(sessionData.expiresAt);
      if (!isNaN(expiresAt.getTime()) && new Date() > expiresAt) {
        localStorage.removeItem("equipr_session");
        toast.error("Session expired. Please login again.");
        navigate("/login");
        return;
      }
    }

    setSession(sessionData);
  }, [navigate]);
  /* eslint-enable */

  // Exit impersonation and return to manager view
  const exitImpersonation = () => {
    if (!session?.impersonating || !session?.originalManager) return;
    
    // Restore original manager session
    const managerSession = {
      ...session.originalManager,
      expires: session.expires,
      expiresAt: session.expiresAt
    };
    
    localStorage.setItem("equipr_session", JSON.stringify(managerSession));
    toast.success("Returned to Manager view");
    // `/dashboard` isn't a registered route (it falls through to a blank
    // screen). Send the manager back to the Agents tab they came from.
    navigate("/agents");
  };

  // Team-lead: hydrate the consolidated "My Team" overview. Called eagerly
  // on initial agent fetch (so the toggle is instant) and manually via the
  // panel's Refresh button.
  const loadTeamOverview = async (leadId) => {
    if (!leadId) return;
    setTeamLoading(true);
    try {
      const res = await axios.get(`${API}/team/overview`, {
        params: { lead_id: leadId, days: 7 },
      });
      setTeamOverview(res.data);
    } catch (err) {
      console.error("Failed to fetch team overview:", err);
      toast.error(err?.response?.data?.detail || "Failed to load My Team data");
    } finally {
      setTeamLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!session) return;
    
    try {
      const [ticketsRes, callsRes, scoresRes, vipRes] = await Promise.all([
        axios.get(`${API}/tickets`),
        axios.get(`${API}/calls`),
        axios.get(`${API}/gamification/scores`),
        axios.get(`${API}/clients/vip-lookup`)
      ]);

      // Store VIP lookup data
      setVipLookup(vipRes.data.vip_lookup || {});

      // Filter tickets assigned to me (match full name, with trimming and case-insensitive)
      const allTickets = ticketsRes.data.tickets || [];
      const fullName = (session.name || "").toLowerCase().trim();
      const jiraDisplayName = (session.jira_display_name || session.name || "").toLowerCase().trim();
      
      const myTicketsFiltered = allTickets.filter(t => {
        const assignee = (t.assignee || "").toLowerCase().trim();
        return assignee === fullName || assignee === jiraDisplayName;
      });
      setMyTickets(myTicketsFiltered);

      // Fetch this agent's CURRENTLY-escalated tickets (active_only filters out
      // tickets that have since been actioned / de-escalated in JIRA).
      try {
        setEscalationsLoading(true);
        const escRes = await axios.get(`${API}/escalations`, {
          params: { agent: session.name, days: 365, limit: 500, active_only: true }
        });
        setMyEscalations(escRes.data.escalations || []);
      } catch (err) {
        console.error("Failed to fetch agent escalations:", err);
      } finally {
        setEscalationsLoading(false);
      }

      // If this user is a team_lead, hydrate the My Team overview eagerly so
      // the panel appears instantly when they toggle to it. Refresh on demand
      // via the panel's "Refresh" button.
      if (session.role === "team_lead") {
        loadTeamOverview(session.id);
      }

      // Pull per-agent queue state in parallel (non-blocking on failure)
      try {
        const emailParam = session?.email || session?.name;
        if (emailParam) {
          const qRes = await axios.get(`${API}/agent/queue`, {
            params: { email: emailParam }
          });
          const map = {};
          (qRes.data.states || []).forEach(s => {
            map[s.ticket_key] = {
              acknowledged_at: s.acknowledged_at,
              queue_position: s.queue_position,
              // Preserve snooze state across refreshes — was being dropped which
              // caused snoozed tickets to bounce back into the Active tab.
              snoozed_until: s.snoozed_until,
            };
          });
          setQueueState(map);
        }
      } catch (e) {
        console.warn("Failed to load queue state:", e);
      }

      // Filter calls for my extension
      const allCalls = callsRes.data.calls || [];
      const myCallsFiltered = allCalls.filter(c => 
        c.assigned_to === session.name || 
        (session.extension && (c.origin === session.extension || c.destination === session.extension))
      );
      setMyCalls(myCallsFiltered);

      // Get my score (match full name or jira display name)
      const scores = scoresRes.data.scores || [];
      const myScoreData = scores.find(s => {
        const scoreName = (s.name || "").toLowerCase().trim();
        return scoreName === fullName || scoreName === jiraDisplayName;
      });
      setMyScore(myScoreData);

      // Stats
      setStats({
        totalTickets: myTicketsFiltered.length,
        openTickets: myTicketsFiltered.filter(t => !t.status?.toLowerCase().includes('resolved') && !t.status?.toLowerCase().includes('closed')).length,
        callsToday: myCallsFiltered.length,
        missedCalls: myCallsFiltered.filter(c => c.is_missed).length,
        callbacksPending: myCallsFiltered.filter(c => c.callback_status === 'pending' || c.callback_status === 'overdue').length
      });

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => {
        clearInterval(interval);
        clearInterval(timeInterval);
      };
    }
  }, [session, fetchData]);

  // Fetch AI motivation when score data is available
  /* eslint-disable */
  useEffect(() => {
    if (session && myScore && !aiMotivation && !loadingMotivation) {
      fetchAIMotivation();
    }
  }, [session, myScore]);
  /* eslint-enable */

  // Refresh motivation every 5 minutes
  useEffect(() => {
    if (session) {
      const motivationInterval = setInterval(() => {
        fetchAIMotivation();
      }, 5 * 60 * 1000);
      return () => clearInterval(motivationInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Fetch stale ticket suggestions when tickets are loaded
  useEffect(() => {
    if (session && myTickets.length > 0) {
      fetchStaleTicketSuggestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, myTickets.length]);

  // Refresh stale tickets every 10 minutes
  useEffect(() => {
    if (session) {
      const staleInterval = setInterval(() => {
        fetchStaleTicketSuggestions();
      }, 10 * 60 * 1000);
      return () => clearInterval(staleInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Fetch handover tickets when tickets are loaded
  /* eslint-disable */
  useEffect(() => {
    if (session && myTickets.length > 0) {
      fetchHandoverTickets();
    }
  }, [session, myTickets.length]);
  /* eslint-enable */

  const handleLogout = () => {
    localStorage.removeItem("equipr_session");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  // Handle ticket escalation
  const handleEscalateTicket = async () => {
    if (!escalatingTicket) return;
    
    // For Kevin Carbe, require target selection
    const isKevinCarbe = session?.name?.toLowerCase().includes("kevin carbe");
    if (isKevinCarbe && !escalationTarget) {
      toast.error("Please select who to escalate to");
      return;
    }
    
    setEscalationLoading(true);
    try {
      const response = await axios.post(`${API}/tickets/${escalatingTicket.key}/escalate`, {
        agent_name: session.name,
        agent_email: session.email,
        reason: escalationReason || null,
        escalation_target: escalationTarget || "Kevin Carbe" // Pass the target
      });
      
      if (response.data.success) {
        toast.success(`Ticket ${escalatingTicket.key} escalated to ${response.data.escalated_to}`);
        setEscalationDialogOpen(false);
        setEscalatingTicket(null);
        setEscalationReason("");
        setEscalationTarget("");
        fetchData(); // Refresh tickets
      } else {
        toast.error("Failed to escalate ticket");
      }
    } catch (error) {
      console.error("Escalation failed:", error);
      toast.error(error.response?.data?.detail || "Failed to escalate ticket");
    } finally {
      setEscalationLoading(false);
    }
  };

  const openEscalationDialog = (ticket) => {
    setEscalatingTicket(ticket);
    setEscalationReason("");
    // Default escalation target based on who is escalating
    if (session?.name?.toLowerCase().includes("kevin carbe")) {
      setEscalationTarget(""); // Kevin must choose
    } else {
      setEscalationTarget("Kevin Carbe"); // Default for other agents
    }
    setEscalationDialogOpen(true);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return ticketSearch || 
           ticketStatusFilter !== "all" || 
           ticketPriorityFilter !== "all" || 
           vipFilterActive || 
           aiSuggestionFilter !== "all";
  };

  // Clear all filters
  const clearAllFilters = () => {
    setTicketSearch("");
    setTicketStatusFilter("all");
    setTicketPriorityFilter("all");
    setVipFilterActive(false);
    setAiSuggestionFilter("all");
    toast.success("Filters cleared");
  };

  // Fetch organizations for ticket creation
  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const res = await axios.get(`${API}/organizations`);
      setOrganizations(res.data.organizations || []);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Fetch JIRA Service Desk request types (so tickets are created as proper requests)
  const fetchRequestTypes = async () => {
    try {
      const res = await axios.get(`${API}/request-types`);
      const types = res.data.request_types || [];
      setRequestTypes(types);
      const defaultId = res.data.default_id || (types[0] && types[0].id) || "";
      setTicketForm((prev) => ({ ...prev, requestTypeId: prev.requestTypeId || defaultId }));
    } catch (error) {
      console.error("Failed to fetch request types:", error);
    }
  };

  // Fetch contacts for a selected organization
  const fetchOrganizationContacts = async (orgName) => {
    if (!orgName) {
      setOrganizationContacts([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/organizations/${encodeURIComponent(orgName)}/contacts`);
      setOrganizationContacts(res.data.contacts || []);
    } catch (error) {
      console.error("Failed to fetch organization contacts:", error);
      setOrganizationContacts([]);
    }
  };

  // Handle refresh tickets button
  const handleRefreshTickets = async () => {
    setRefreshingTickets(true);
    try {
      // Trigger a sync and fetch fresh data
      await axios.post(`${API}/sync`);
      await fetchData();
      toast.success("Tickets refreshed");
    } catch (error) {
      console.error("Failed to refresh tickets:", error);
      toast.error("Failed to refresh tickets");
    } finally {
      setRefreshingTickets(false);
    }
  };

  // Open ignore dialog for an AI suggestion
  const openIgnoreDialog = (ticket, suggestion) => {
    setIgnoringSuggestion({
      ticket_key: ticket.key,
      ticket_status: ticket.status,
      suggestion_action: suggestion.action,
      suggestion_reason: suggestion.reason,
      linked_issues: ticket.linked_issues || []
    });
    setIgnoreCategory("");
    setIgnoreNotes("");
    setShowIgnoreDialog(true);
  };

  // Submit ignore feedback
  const handleIgnoreSuggestion = async () => {
    if (!ignoreCategory) {
      toast.error("Please select a reason");
      return;
    }

    setSubmittingIgnore(true);
    try {
      await axios.post(`${API}/ai/suggestions/ignore`, {
        ticket_key: ignoringSuggestion.ticket_key,
        suggestion_action: ignoringSuggestion.suggestion_action,
        suggestion_reason: ignoringSuggestion.suggestion_reason,
        agent_name: session?.name || session?.jira_display_name,
        ignore_category: ignoreCategory,
        ignore_notes: ignoreNotes || null,
        ticket_status: ignoringSuggestion.ticket_status,
        linked_issues: ignoringSuggestion.linked_issues
      });

      // Remove from local state
      setTicketSuggestions(prev => {
        const updated = { ...prev };
        delete updated[ignoringSuggestion.ticket_key];
        return updated;
      });

      toast.success("Feedback recorded - suggestion hidden");
      setShowIgnoreDialog(false);
    } catch (error) {
      console.error("Failed to record ignore:", error);
      toast.error("Failed to record feedback");
    } finally {
      setSubmittingIgnore(false);
    }
  };

  const openCreateTicket = (call = null) => {
    setSelectedCall(call);
    setTicketForm({
      product: "Allocate + Operator",
      requestTypeId: "",
      summary: call ? `Call from ${call.origin || call.destination} - ` : "",
      description: call ? `Call Details:\n- Time: ${call.datetime}\n- Duration: ${call.duration_hr || call.duration + 's'}\n- Direction: ${call.direction === 'IN' ? 'Inbound' : 'Outbound'}\n\nIssue Description:\n` : "",
      priority: "3",
      organizationId: "",
      reporterEmail: ""
    });
    setOrganizationContacts([]);
    fetchOrganizations();
    fetchRequestTypes();
    setShowCreateTicket(true);
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.summary.trim()) {
      toast.error("Please enter a summary");
      return;
    }
    if (!ticketForm.organizationId) {
      toast.error("Please select an organisation");
      return;
    }
    if (!ticketForm.reporterEmail) {
      toast.error("Please select a reporter so the customer receives email updates");
      return;
    }

    setCreatingTicket(true);
    
    try {
      const res = await axios.post(`${API}/tickets/create`, {
        project: "SUPPORT",
        issueType: ticketForm.requestTypeId,
        requestTypeId: ticketForm.requestTypeId || undefined,
        summary: ticketForm.summary,
        description: ticketForm.description,
        priority: ticketForm.priority,
        assignee: session.jira_display_name || session.name,
        callId: selectedCall?.callid,
        organization: ticketForm.organizationId || undefined,
        reporterEmail: ticketForm.reporterEmail || undefined
      });

      if (res.data.success) {
        const newKey = res.data.ticket_key;
        // Upload attachments if any selected
        if (ticketAttachments.length > 0) {
          try {
            const fd = new FormData();
            ticketAttachments.forEach(f => fd.append("files", f));
            await axios.post(`${API}/tickets/${newKey}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" }
            });
            toast.success(`Ticket ${newKey} created with ${ticketAttachments.length} attachment${ticketAttachments.length === 1 ? "" : "s"}`);
          } catch (attachErr) {
            console.error("Attachment upload failed:", attachErr);
            toast.error(`Ticket ${newKey} created, but attachments failed to upload`);
          }
        } else {
          toast.success(`Ticket ${newKey} created!`);
        }
        setTicketAttachments([]);
        setShowCreateTicket(false);
        fetchData();
      } else {
        toast.error(res.data.error || "Failed to create ticket");
      }
    } catch (error) {
      console.error("Failed to create ticket:", error);
      toast.error("Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Daily goals calculation
  const dailyGoals = [
    { 
      label: "Tickets Resolved", 
      current: myScore?.tickets_resolved || 0, 
      target: 5,
      icon: CheckCircle 
    },
    { 
      label: "SLA Compliance", 
      current: myScore?.sla_compliance || 100, 
      target: 95,
      unit: "%",
      icon: Target 
    },
    { 
      label: "Callbacks Returned", 
      current: myScore?.callback_compliance || 100, 
      target: 100,
      unit: "%",
      icon: Phone 
    },
    { 
      label: "No Idle Tickets", 
      current: myTickets.filter(t => t.idle_hours < 4).length, 
      target: myTickets.length || 1,
      icon: Clock 
    }
  ];

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-[#EDEAE3]" data-testid="agent-dashboard-loading">
        {/* Chrome header skeleton */}
        <div className="bg-[#121212] border-b border-[#2B2B2B] px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="h-6 w-44 rounded bg-[#2B2B2B] animate-pulse" />
            <div className="h-5 w-24 rounded-full bg-[#1E1E1E] animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
          {/* Workload strip skeleton */}
          <div className="h-16 rounded-xl bg-[#F6F4EF] border border-[#DAD5CA] animate-pulse" />
          {/* Queue card skeleton with rows at real row height */}
          <div className="rounded-xl bg-[#FFFFFF] border border-[#DAD5CA] p-4">
            <div className="h-[42px] w-full max-w-md rounded-[9px] bg-[#F6F4EF] animate-pulse mb-4" />
            <div className="space-y-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-11 rounded-lg bg-[#F6F4EF] border border-[#EFECE4] animate-pulse"
                  style={{ animationDelay: `${i * 70}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Shared props for the directive queue table (used inline + fullscreen)
  const queueTableProps = {
    tickets: myTickets,
    vipLookup,
    ticketSuggestions,
    isSnoozed,
    isAcknowledged,
    jiraUrl: JIRA_URL,
    productTab, setProductTab,
    queueTab, setQueueTab,
    search: ticketSearch, setSearch: setTicketSearch,
    vipActive: vipFilterActive, setVipActive: setVipFilterActive,
    statusFilter: ticketStatusFilter, setStatusFilter: setTicketStatusFilter,
    priorityFilter: ticketPriorityFilter, setPriorityFilter: setTicketPriorityFilter,
    aiFilter: aiSuggestionFilter, setAiFilter: setAiSuggestionFilter,
    myEscalationsCount: myEscalations.length,
    renderEscalations: () => (
      <EscalationsPanel
        escalations={myEscalations}
        loading={escalationsLoading}
        onOpenRunbook={(org) => setRunbookCompany(org)}
        jiraUrl={JIRA_URL}
      />
    ),
    triageKey: selectedTicketForSuggestions,
    onTriage: (key) => fetchResolutionSuggestions(key),
    onStart: (key) => handleStartTicket(key),
    onUnsnooze: (key) => unsnoozeTicket(key),
    onSnooze: (t) => setConfirmAction({
      title: `Snooze ${t.key}?`,
      description: "The ticket leaves your active queue and stays snoozed until you wake it up — it will not resurface on its own. Use this only when you're genuinely blocked.",
      confirmLabel: "Snooze ticket",
      onConfirm: () => snoozeTicket(t.key),
    }),
    onSendBottom: (t) => setConfirmAction({
      title: `Send ${t.key} to the bottom?`,
      description: "This moves the ticket to the very bottom of your active queue so everything else is worked first. You can reprioritise it again at any time.",
      confirmLabel: "Send to bottom",
      onConfirm: () => sendTicketToBottom(t.key),
    }),
    onEscalate: (t) => openEscalationDialog(t),
    onIgnoreSuggestion: openIgnoreDialog,
    onCloseStale: handleCloseStaleTicket,
    onOpenRunbook: (org) => setRunbookCompany(org),
    onAckSilent: (key) => ackTicket(key, { silent: true }),
    selectedNewKeys, toggleSelectKey, selectAllVisibleNew, clearSelection,
    bulkMoveSelectedToActive, bulkAcking,
  };

  return (
    <div className="min-h-screen bg-[#EDEAE3]" data-testid="agent-dashboard" data-agent-theme={agentTheme}>
      {/* Impersonation Banner — safety marker (§3.4) */}
      {session?.impersonating && (
        <div className="bg-[#FFEDE4] border-b-[3px] border-[#FF4F00] px-6 py-2.5 flex items-center justify-between gap-4" data-testid="impersonation-banner">
          <div className="flex items-center gap-2.5 text-[#8F2E00]">
            <Eye className="w-4 h-4 shrink-0" />
            <span className="text-[12.5px] font-bold">
              You are viewing the application as another user.
            </span>
            <span className="text-[12.5px] font-medium">
              Every action you take is recorded against <strong>{session.name}</strong>, not you.
            </span>
            <span className="text-[12.5px] text-[#8F2E00]/80">
              signed in as <span className="mono">{session.originalManager?.name || "you"}</span>
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={exitImpersonation}
            className="border-[#C9C3B6] bg-[#FFFFFF] hover:bg-[#E4E0D7] text-[#1A1A1A] h-8 px-3 text-xs font-semibold shrink-0"
            data-testid="exit-impersonation-btn"
          >
            <X className="w-3 h-3 mr-1.5" />
            Return to my account
          </Button>
        </div>
      )}
      
      {/* Header — chrome (§ item 10) */}
      <header className="bg-[#121212] border-b border-[#2B2B2B] px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-[#F5F5F5]">EQUIPR</span> <span className="bg-[#FFD108] text-[#1A1A1A] px-1.5 rounded">SUPPORT</span>
            </h1>
            <span className="text-[10px] uppercase tracking-[0.08em] text-[#8A8A8A] border border-[#333333] rounded px-1.5 py-0.5">Agent View</span>
            {session?.role === "team_lead" && (
              <div className="flex items-center gap-1 bg-[#1C1C1C] rounded-md p-0.5" data-testid="team-view-toggle">
                <button
                  onClick={() => setPageView("my_tickets")}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    pageView === "my_tickets" ? "bg-[#FFD108] text-[#000000]" : "text-[#B8B4AC] hover:text-[#FFFFFF]"
                  }`}
                  data-testid="page-view-my-tickets"
                >
                  My Tickets
                </button>
                <button
                  onClick={() => {
                    setPageView("my_team");
                    if (!teamOverview && !teamLoading) loadTeamOverview(session.id);
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${
                    pageView === "my_team" ? "bg-[#FFD108] text-black" : "text-[#B8B4AC] hover:text-[#FFFFFF]"
                  }`}
                  data-testid="page-view-my-team"
                >
                  <Users className="w-3 h-3" />
                  My Team
                  {teamOverview?.inbox?.length > 0 && (
                    <span className="bg-[#FF4F00] text-[#FFFFFF] text-[10px] px-1.5 rounded-full">
                      {teamOverview.inbox.length}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-6">
            <AtlassianChip />
            {/* My Work — personal cross-module hub */}
            <Link
              to="/my-work"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1C1C1C] text-sm font-medium text-[#B8B4AC] hover:text-[#FFFFFF] transition-colors"
              data-testid="agent-my-work-btn"
            >
              <ClipboardCheck className="w-4 h-4" />
              My Work
            </Link>
            {/* Manager View Toggle - show for managers */}
            {session?.role === "manager" && (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium text-[#B8B4AC] hover:text-[#FFFFFF] hover:bg-[#1E1E1E] transition-colors"
                data-testid="switch-to-manager-btn"
              >
                <LayoutDashboard className="w-4 h-4" />
                Manager View
              </Link>
            )}
            
            {/* Ask The Code — query the indexed GitHub repositories (internal only) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAskCode(true)}
              className="border-[#333333] bg-transparent text-[#B8B4AC] hover:text-[#FFFFFF] hover:bg-[#1E1E1E]"
              data-testid="open-ask-code-btn"
            >
              <Code2 className="w-4 h-4 mr-2" />
              Ask The Code
            </Button>
            
            {/* Queue Health — chrome chip (§ item 8) */}
            <button
              onClick={() => setShowQueueHealth(true)}
              className="flex items-center gap-2 hover:bg-[#1E1E1E] rounded-full px-3 py-1.5 transition-colors border border-[#2B2B2B]"
              data-testid="queue-health-header"
              title="Queue Health — based on how long your Waiting for customer / support tickets have been sitting"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: queueHealth.color }} />
              <span className="text-[11px] uppercase tracking-wide text-[#8A8A8A]">Queue Health</span>
              <span className="mono text-sm font-semibold text-[#F5F5F5]">{queueHealth.score}</span>
            </button>
            
            {/* Theme toggle — sun / moon (directive §7), persists per browser */}
            <div className="flex items-center bg-[#1C1C1C] rounded-full p-0.5 border border-[#2B2B2B]" data-testid="agent-theme-toggle">
              <button
                onClick={() => setAgentTheme("light")}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${agentTheme === "light" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#8A8A8A] hover:text-[#FFFFFF]"}`}
                title="Light theme" data-testid="theme-light-btn"
              >
                <Sun className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setAgentTheme("dark")}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${agentTheme === "dark" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#8A8A8A] hover:text-[#FFFFFF]"}`}
                title="Dark theme" data-testid="theme-dark-btn"
              >
                <Moon className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Agent Info */}
            <div className="flex items-center gap-3 pl-6 border-l border-[#2B2B2B]">
              <div className="w-8 h-8 rounded-full bg-[#FFD108] flex items-center justify-center text-[#000000] font-bold text-xs">
                {session.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="text-sm font-medium text-[#F5F5F5]">{session.name}</p>
                <p className="text-xs text-[#8A8A8A]">Ext: {session.extension || 'N/A'}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="ml-2 text-[#8A8A8A] hover:text-[#FFFFFF] hover:bg-[#1E1E1E]"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ask The Code — query the indexed GitHub repositories */}
      <AskTheCodeDrawer open={showAskCode} onOpenChange={setShowAskCode} />

      {/* Navigator quick-access sidebar (#11) */}
      <Sheet open={showNavigator} onOpenChange={setShowNavigator}>
        <SheetContent side="left" className="bg-[#FFFFFF] border-[#DAD5CA] w-72" data-testid="navigator-sidebar">
          <SheetHeader>
            <SheetTitle className="text-[#1A1A1A] flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-[#8A6D00]" /> Navigator
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-1">
            {[
              { label: "SQL CoPilot", desc: "Generate read-only SQL", icon: Sparkles, color: "#1D4ED8", onClick: () => { setShowNavigator(false); setShowSqlCopilot(true); } },
              { label: "Client Runbooks", desc: "Configs & secrets vault", icon: BookOpen, color: "#0E6B3F", onClick: () => navigate("/runbooks") },
              { label: "Command Centre", desc: "Live ops wallboard", icon: LayoutDashboard, color: "#6B5600", onClick: () => navigate("/command-centre") },
              { label: "Companies", desc: "Client directory", icon: Building2, color: "#8A6D00", onClick: () => navigate("/companies") },
              { label: "Knowledge Base", desc: "Search resolved tickets", icon: Lightbulb, color: "#C9A400", onClick: () => { setShowNavigator(false); setShowAIAssistant(true); } },
            ].map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F4F2ED] transition-colors text-left group"
                data-testid={`navigator-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}20` }}>
                  <item.icon className="w-4 h-4" style={{ color: item.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{item.label}</p>
                  <p className="text-xs text-[#8A857A] truncate">{item.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#8A857A] ml-auto group-hover:text-[#1A1A1A] transition-colors" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Client runbook (modal, opened from the queue client link) */}
      <RunbookModal
        open={!!runbookCompany}
        onOpenChange={(o) => { if (!o) setRunbookCompany(null); }}
        company={runbookCompany}
        agentTheme={agentTheme}
      />

      {/* Queue Health breakdown dialog */}
      <Dialog open={showQueueHealth} onOpenChange={setShowQueueHealth}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="queue-health-dialog" data-agent-theme={agentTheme}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" style={{ color: queueHealth.color }} />
              Queue Health — {queueHealth.label} ({queueHealth.score})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#59554D]">
              Based on how long your <span className="text-[#1A1A1A]">Waiting for customer</span> and{' '}
              <span className="text-[#1A1A1A]">Waiting for support</span> tickets have been sitting. Long-waiting
              tickets drag your score down — chase them, or close stale ones as Resolved Without Email.
            </p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-[#F4F2ED] rounded-lg">
                <p className="text-xl font-bold text-[#1A1A1A]">{queueHealth.total_waiting}</p>
                <p className="text-xs text-[#59554D]">Waiting total</p>
              </div>
              <div className="p-3 bg-[#F4F2ED] rounded-lg">
                <p className="text-xl font-bold text-[#1A1A1A]">{queueHealth.waiting_for_customer}</p>
                <p className="text-xs text-[#59554D]">On customer</p>
              </div>
              <div className="p-3 bg-[#F4F2ED] rounded-lg">
                <p className="text-xl font-bold text-[#1A1A1A]">{queueHealth.waiting_for_support}</p>
                <p className="text-xs text-[#59554D]">On support</p>
              </div>
            </div>
            {queueHealth.breakdown.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#0E6B3F]" />
                <p className="text-sm text-[#59554D]">Your waiting queue is clean — nothing stale.</p>
              </div>
            ) : (
              <ScrollArea className="max-h-72">
                <div className="space-y-2 pr-3">
                  {queueHealth.breakdown.map((item) => (
                    <div key={item.key} className="p-3 bg-[#F4F2ED] rounded-lg border border-[#DAD5CA]" data-testid={`queue-health-item-${item.key}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <a href={`${JIRA_URL}/${item.key}`} target="_blank" rel="noopener noreferrer" className="text-[#8A6D00] hover:underline font-mono text-sm">{item.key}</a>
                        <Badge className={item.severity === 'critical' ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#FFF7D1] text-[#6B5600]'}>
                          {item.status} · {item.business_days}d
                        </Badge>
                      </div>
                      <p className="text-sm text-[#1A1A1A] truncate">{item.summary}</p>
                      <p className="text-xs text-[#8A857A] mt-1">{item.recommendation}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="p-6 space-y-6">
        {pageView === "my_team" ? (
          <MyTeamPanel
            overview={teamOverview}
            loading={teamLoading}
            onRefresh={() => loadTeamOverview(session.id)}
            onOpenRunbook={(org) => navigate(`/runbooks?company=${encodeURIComponent(org)}`)}
            jiraUrl={JIRA_URL}
          />
        ) : (
        <>
        {/* My Workload — rich overview card (collapsible, mirrors manager's Agent Workload card) */}
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="my-workload-card">
          <CardContent className="p-5">
            {/* Header: identity + secondary stats + queue health gauge + collapse toggle */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#FFF8D6] flex items-center justify-center text-[#8A6D00] font-medium">
                  {session.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A] text-lg">My Workload</p>
                  <p className="text-xs text-[#59554D]">{session.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 sm:gap-5">
                {/* Compact inline metrics — shown only when collapsed */}
                {workloadCollapsed && (
                  <div className="hidden md:flex items-center gap-4 text-sm">
                    <span className="text-[#59554D]">Open <span className="text-[#1A1A1A] font-bold">{myWorkload.open}</span></span>
                    <span className="text-[#59554D]">High Pri <span className="text-[#C23A00] font-bold">{myWorkload.high}</span></span>
                    <span className="text-[#59554D]">Avg Idle <span className="text-[#1A1A1A] font-bold">{myWorkload.avgIdle}h</span></span>
                  </div>
                )}
                {/* Calls / Callbacks — shown only when expanded */}
                {!workloadCollapsed && (
                  <div className="hidden sm:flex items-center gap-5">
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#1A1A1A]">{stats?.callsToday || 0}</p>
                      <p className="text-[10px] text-[#59554D]">Calls Today</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-xl font-bold ${stats?.callbacksPending > 0 ? 'text-[#C23A00]' : 'text-[#0E6B3F]'}`}>{stats?.callbacksPending || 0}</p>
                      <p className="text-[10px] text-[#59554D]">Callbacks Due</p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={toggleWorkloadCollapsed}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[#59554D] hover:text-[#1A1A1A] hover:bg-[#F4F2ED] transition-colors"
                  title={workloadCollapsed ? "Expand workload" : "Collapse workload"}
                  data-testid="toggle-workload-collapse"
                >
                  {workloadCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {!workloadCollapsed && (
              <>
                {/* Main metrics grid */}
                <div className="grid grid-cols-3 gap-4 mt-5 pb-4 border-b border-[#DAD5CA]">
                  <div>
                    <p className="metric-label">Open</p>
                    <p className="text-2xl font-bold text-[#1A1A1A]">{myWorkload.open}</p>
                  </div>
                  <div>
                    <p className="metric-label">High Priority</p>
                    <p className="text-2xl font-bold text-[#C23A00]">{myWorkload.high}</p>
                  </div>
                  <div>
                    <p className="metric-label">Avg Idle</p>
                    <p className="text-2xl font-bold text-[#1A1A1A]">{myWorkload.avgIdle}h</p>
                  </div>
                </div>

                {/* By Status — clickable chips that filter My Open Tickets below */}
                {Object.keys(myWorkload.statusBreakdown).length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="metric-label">By Status <span className="text-[#8A857A]">— click to filter</span></p>
                      {ticketStatusFilter !== "all" && (
                        <button
                          onClick={() => setTicketStatusFilter("all")}
                          className="text-[10px] text-[#8A6D00] hover:underline"
                          data-testid="clear-status-filter"
                        >
                          Clear filter
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="my-workload-status-breakdown">
                      {Object.entries(myWorkload.statusBreakdown)
                        .sort((a, b) => b[1] - a[1])
                        .map(([status, count]) => {
                          const sl = status.toLowerCase();
                          const dot = sl.includes("escalat") ? "#C23A00"
                            : sl.includes("customer") ? "#6B5600"
                            : sl.includes("support") ? "#8A6D00"
                            : sl.includes("system") ? "#1D4ED8"
                            : sl.includes("development") ? "#1D4ED8"
                            : sl === "new" ? "#0E6B3F"
                            : "#59554D";
                          const isActive = ticketStatusFilter !== "all" && ticketStatusFilter.toLowerCase() === sl;
                          return (
                            <button
                              key={status}
                              type="button"
                              onClick={() => {
                                setTicketStatusFilter(isActive ? "all" : status);
                                document.getElementById("my-open-tickets-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }}
                              className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2 py-0.5 border transition-colors ${isActive ? 'bg-[#FFF8D6] border-[#8A6D00]' : 'bg-[#F4F2ED] border-[#DAD5CA] hover:border-[#C9C3B6]'}`}
                              title={`Filter to ${status}`}
                              data-testid={`status-chip-${sl.replace(/\s+/g, '-')}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot }} />
                              <span className={`truncate max-w-[160px] ${isActive ? 'text-[#1A1A1A]' : 'text-[#59554D]'}`}>{status}</span>
                              <span className="text-[#1A1A1A] font-semibold">{count}</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Open by Client */}
                {myWorkload.clients.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#DAD5CA]">
                    <p className="metric-label mb-2">Open by Client ({myWorkload.clients.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 max-h-40 overflow-y-auto pr-1" data-testid="my-workload-clients">
                      {myWorkload.clients.map((c) => (
                        <div key={c.name} className="flex items-center justify-between text-xs gap-2">
                          <span className="text-[#59554D] truncate flex items-center gap-1.5 min-w-0">
                            <Building2 className="w-3 h-3 text-[#8A857A] shrink-0" />
                            <span className="truncate">{c.name}</span>
                          </span>
                          <span className="text-[#1A1A1A] font-semibold bg-[#F4F2ED] rounded px-1.5 min-w-[22px] text-center shrink-0">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* AI Motivation Banner - Prominently placed at top */}
        <div className="relative" data-testid="ai-motivation-banner">
          <div className="rounded-xl overflow-hidden transition-all duration-300 bg-[#FBFAF7] border border-[#DAD5CA]">
            <div className="px-4 py-3">
              {loadingMotivation ? (
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-[#8A6D00]" />
                  <span className="text-[#59554D] text-sm">Getting your personalized insights...</span>
                </div>
              ) : aiMotivation ? (
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    aiMotivation.type === 'celebration' ? 'bg-[#FFF8D6]' :
                    aiMotivation.type === 'encouragement' ? 'bg-[#E3F3E9]' :
                    aiMotivation.type === 'tip' ? 'bg-[#FFF8D6]' :
                    aiMotivation.type === 'focus' ? 'bg-[#FFF7D1]' :
                    'bg-[#EFF6FF]'
                  }`}>
                    {aiMotivation.type === 'celebration' ? <Trophy className="w-5 h-5 text-[#C9A400]" /> :
                     aiMotivation.type === 'encouragement' ? <Heart className="w-5 h-5 text-[#0E6B3F]" /> :
                     aiMotivation.type === 'tip' ? <Lightbulb className="w-5 h-5 text-[#8A6D00]" /> :
                     aiMotivation.type === 'focus' ? <Target className="w-5 h-5 text-[#59554D]" /> :
                     <Sparkles className="w-5 h-5 text-[#1D4ED8]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1A1A1A] text-sm leading-relaxed line-clamp-2">{aiMotivation.message}</p>
                    {aiMotivation.tip && (
                      <p className="mt-1 text-xs text-[#59554D] flex items-center gap-1.5">
                        <Zap className="w-3 h-3 text-[#C9A400] shrink-0" />
                        <span className="line-clamp-1">{aiMotivation.tip}</span>
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchAIMotivation}
                    className="text-[#59554D] hover:text-[#1A1A1A] shrink-0"
                    title="Get new insight"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingMotivation ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Coffee className="w-5 h-5 text-[#59554D]" />
                  <span className="text-[#59554D] text-sm">AI insights will appear here once data loads...</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="flex flex-col gap-6" style={{ minHeight: 'calc(100vh - 280px)' }}>
          {/* Center & Right - My Tickets */}
          <div className="flex flex-col gap-4 min-h-0 flex-1">
            {/* My Open Tickets */}
            {/* My Open Tickets */}
            {ticketsFullscreen && (
              <div className="fixed inset-0 z-50 bg-[#FFFFFF] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-[#DAD5CA]">
                  <div className="flex items-center gap-3">
                    <Ticket className="w-6 h-6 text-[#8A6D00]" />
                    <h2 className="text-xl font-semibold text-[#1A1A1A]">My Open Tickets</h2>
                    <Badge className="bg-[#F4F2ED] text-[#59554D]">{stats?.openTickets || 0}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {staleTicketsStatus === 'loading' && (
                      <div className="flex items-center gap-2 text-xs text-[#59554D] bg-[#F4F2ED] px-3 py-1.5 rounded-full">
                        <Loader2 className="w-3 h-3 animate-spin text-[#1D4ED8]" />
                        <span>AI analyzing...</span>
                      </div>
                    )}
                    {staleTicketsStatus === 'ready' && Object.keys(ticketSuggestions).length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-[#0E6B3F] bg-[#E3F3E9] px-3 py-1.5 rounded-full">
                        <Sparkles className="w-3 h-3" />
                        <span>{Object.keys(ticketSuggestions).length} AI</span>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => openCreateTicket()}
                      className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] h-9 px-4"
                      data-testid="new-ticket-btn-fs"
                    >
                      <Plus className="w-4 h-4 mr-1" /> New Ticket
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRefreshTickets}
                      disabled={refreshingTickets}
                      className="text-[#8A6D00] hover:text-[#1A1A1A] hover:bg-[#FFF8D6] h-8 px-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingTickets ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setTicketsFullscreen(false)}
                      className="text-[#59554D] hover:text-[#1A1A1A] hover:bg-[#DAD5CA] h-8 px-2"
                      data-testid="exit-fullscreen-btn"
                    >
                      <Minimize2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {/* Fullscreen queue body — directive table */}
                <div className="flex-1 overflow-hidden px-4 pb-4 flex flex-col min-h-0">
                  <AgentQueueTable fullscreen {...queueTableProps} />
                </div>
              </div>
            )}
            <Card id="my-open-tickets-panel" className={`bg-[#FFFFFF] border-[#DAD5CA] flex-1 flex flex-col min-h-0 ${ticketsFullscreen ? 'hidden' : ''}`}>
              <CardHeader className="pb-2 flex-shrink-0">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-[#8A6D00]" /> My Open Tickets
                    <Badge className="bg-[#F4F2ED] text-[#59554D]">{stats?.openTickets || 0}</Badge>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* AI Analysis Status Indicator */}
                    {staleTicketsStatus === 'loading' && (
                      <div className="flex items-center gap-2 text-xs text-[#59554D] bg-[#F4F2ED] px-3 py-1.5 rounded-full">
                        <Loader2 className="w-3 h-3 animate-spin text-[#1D4ED8]" />
                        <span>AI analyzing...</span>
                      </div>
                    )}
                    {staleTicketsStatus === 'refreshing' && (
                      <div className="flex items-center gap-2 text-xs text-[#59554D] bg-[#F4F2ED] px-3 py-1.5 rounded-full">
                        <Loader2 className="w-3 h-3 animate-spin text-[#1D4ED8]" />
                        <span>Refreshing...</span>
                      </div>
                    )}
                    {staleTicketsStatus === 'ready' && Object.keys(ticketSuggestions).length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-[#0E6B3F] bg-[#E3F3E9] px-3 py-1.5 rounded-full">
                        <Sparkles className="w-3 h-3" />
                        <span>{Object.keys(ticketSuggestions).length} AI</span>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => openCreateTicket()}
                      className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] h-9 px-4"
                      data-testid="new-ticket-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> New Ticket
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRefreshTickets}
                      disabled={refreshingTickets}
                      className="text-[#8A6D00] hover:text-[#1A1A1A] hover:bg-[#FFF8D6] h-8 w-8 p-0"
                      title="Refresh ticket data from JIRA"
                      data-testid="refresh-tickets-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingTickets ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setTicketsFullscreen(true)}
                      className="text-[#59554D] hover:text-[#1A1A1A] hover:bg-[#DAD5CA] h-8 w-8 p-0"
                      title="Fullscreen mode"
                      data-testid="fullscreen-tickets-btn"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden min-h-0">
                <AgentQueueTable {...queueTableProps} />
                            </CardContent>
            </Card>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Confirm-before-consequence dialog (§ directive item 11) */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="confirm-action-dialog" data-agent-theme={agentTheme}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{confirmAction?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#59554D] py-1">{confirmAction?.description}</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"
              data-testid="confirm-action-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { const fn = confirmAction?.onConfirm; setConfirmAction(null); fn && fn(); }}
              className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-medium"
              data-testid="confirm-action-confirm"
            >
              {confirmAction?.confirmLabel || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore AI Suggestion Dialog */}
      <Dialog open={showIgnoreDialog} onOpenChange={(open) => { if (!open) { setIgnoringSuggestion(null); setIgnoreCategory(""); setIgnoreNotes(""); } setShowIgnoreDialog(open); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Ban className="w-5 h-5 text-[#59554D]" />
              Ignore AI Suggestion
            </DialogTitle>
          </DialogHeader>
          
          {ignoringSuggestion && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-[#F4F2ED] rounded-lg">
                <p className="text-sm text-[#59554D]">Ticket: <span className="text-[#1A1A1A] font-medium">{ignoringSuggestion.ticket_key}</span></p>
                <p className="text-sm text-[#59554D] mt-1">Suggestion: <span className="text-[#59554D]">{ignoringSuggestion.suggestion_action?.replace(/_/g, ' ')}</span></p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#59554D]">Why are you ignoring this suggestion? *</Label>
                <Select value={ignoreCategory} onValueChange={setIgnoreCategory}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectItem value="already_handled">Already handled / In progress</SelectItem>
                    <SelectItem value="not_applicable">Not applicable to this ticket</SelectItem>
                    <SelectItem value="incorrect">Suggestion is incorrect</SelectItem>
                    <SelectItem value="will_action_later">Will action later</SelectItem>
                    <SelectItem value="customer_preference">Customer preference / special case</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#59554D]">Additional notes (optional)</Label>
                <Textarea
                  value={ignoreNotes}
                  onChange={(e) => setIgnoreNotes(e.target.value)}
                  placeholder="Help us improve - what would be the right suggestion?"
                  className="bg-[#F4F2ED] border-[#DAD5CA] min-h-[80px]"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowIgnoreDialog(false)}
                  className="flex-1 border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#DAD5CA] hover:text-[#1A1A1A]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleIgnoreSuggestion}
                  disabled={!ignoreCategory || submittingIgnore}
                  className="flex-1 bg-[#FFD108] hover:bg-[#E0B800] text-black"
                >
                  {submittingIgnore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Feedback"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Escalation Dialog */}
      <Dialog open={escalationDialogOpen} onOpenChange={(open) => { if (!open) { setEscalatingTicket(null); setEscalationReason(""); } setEscalationDialogOpen(open); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-[#59554D]" />
              Escalate Ticket
            </DialogTitle>
          </DialogHeader>
          
          {escalatingTicket && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F4F2ED] rounded-lg border border-[#DAD5CA]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[#8A6D00] font-mono text-sm">{escalatingTicket.key}</span>
                  <Badge className="bg-[#DAD5CA] text-[#59554D] text-xs">{escalatingTicket.priority || 'Medium'}</Badge>
                </div>
                <p className="text-sm text-[#1A1A1A] truncate">{escalatingTicket.summary}</p>
                <p className="text-xs text-[#8A857A] mt-1">{escalatingTicket.organization || 'Unknown'}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#59554D]">Reason for Escalation (optional)</Label>
                <Input
                  value={escalationReason}
                  onChange={(e) => setEscalationReason(e.target.value)}
                  placeholder="e.g., Need senior expertise for this issue"
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                />
              </div>
              
              <div className="p-3 bg-[#FFF7D1] rounded-lg border border-[#FFF7D1]">
                <p className="text-sm text-[#59554D]">
                  This ticket will be escalated to <strong>Kevin Carbe</strong> with an AI-generated summary of the issue.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEscalationDialogOpen(false)}
                  className="border-[#DAD5CA] text-[#1A1A1A]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEscalateTicket}
                  disabled={escalationLoading}
                  className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]"
                  data-testid="confirm-escalate-btn"
                >
                  {escalationLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Escalating...
                    </>
                  ) : (
                    <>
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      Escalate
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Handover Summary Dialog */}
      <Dialog open={!!selectedHandover} onOpenChange={(open) => { if (!open) { setSelectedHandover(null); setHandoverSummary(null); } }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="handover-description">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[#1D4ED8]" />
              Ticket Handover Summary
            </DialogTitle>
            <p id="handover-description" className="sr-only">AI-generated summary of previous work on this ticket</p>
          </DialogHeader>
          
          {loadingHandoverSummary ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1D4ED8] mb-4" />
              <p className="text-[#59554D]">Analyzing ticket history...</p>
              <p className="text-xs text-[#8A857A] mt-1">This may take a few moments</p>
            </div>
          ) : handoverSummary ? (
            <div className="space-y-4">
              {/* Ticket Header */}
              <div className="flex items-center justify-between p-3 bg-[#F4F2ED] rounded-lg">
                <div>
                  <a
                    href={`${JIRA_URL}/${handoverSummary.ticket_key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#8A6D00] hover:underline font-mono"
                  >
                    {handoverSummary.ticket_key}
                  </a>
                  <p className="text-[#1A1A1A] text-sm mt-1">{handoverSummary.summary}</p>
                </div>
                <div className="text-right">
                  <Badge className={
                    handoverSummary.priority === "Highest" ? "bg-[#FFE9E0] text-[#C23A00]" :
                    handoverSummary.priority === "High" ? "bg-[#FFF7D1] text-[#6B5600]" :
                    "bg-[#DAD5CA] text-[#59554D]"
                  }>
                    {handoverSummary.priority}
                  </Badge>
                  <p className="text-xs text-[#59554D] mt-1">{handoverSummary.status}</p>
                </div>
              </div>

              {/* AI Badge */}
              {handoverSummary.ai_generated && (
                <div className="flex items-center gap-2 text-xs text-[#1D4ED8]">
                  <Sparkles className="w-3 h-3" />
                  <span>AI-Generated Summary</span>
                </div>
              )}

              {/* Issue Summary */}
              <div className="p-4 bg-gradient-to-br from-[#FFF8D6] to-[#EFF6FF] rounded-lg border border-[#FFF8D6]">
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#C9A400]" />
                  What&apos;s This About
                </h4>
                <p className="text-sm text-[#59554D]">{handoverSummary.handover_summary}</p>
              </div>

              {/* Work Done */}
              {handoverSummary.work_done && (
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0E6B3F]" />
                    What&apos;s Been Done
                  </h4>
                  <p className="text-sm text-[#59554D]">{handoverSummary.work_done}</p>
                </div>
              )}

              {/* Current State */}
              {handoverSummary.current_state && (
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#8A6D00]" />
                    Where Things Stand
                  </h4>
                  <p className="text-sm text-[#59554D]">{handoverSummary.current_state}</p>
                </div>
              )}

              {/* Key People */}
              {handoverSummary.previous_agents && handoverSummary.previous_agents.length > 0 && (
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#1D4ED8]" />
                    Previously Worked On By
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {handoverSummary.previous_agents.map((agent, idx) => (
                      <Badge key={idx} className="bg-[#EFF6FF] text-[#1D4ED8]">
                        {agent}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested First Action */}
              <div className="p-4 bg-gradient-to-br from-[#E3F3E9] to-[#E3F3E9] rounded-lg border border-[#E3F3E9]">
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-[#0E6B3F]" />
                  Suggested First Action
                </h4>
                <p className="text-sm text-[#0E6B3F]">{handoverSummary.suggested_first_action}</p>
              </div>

              {/* Priority Notes */}
              {handoverSummary.priority_notes && (
                <div className="p-3 bg-[#FFF7D1] rounded-lg border border-[#FFF7D1]">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#59554D] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#59554D]">{handoverSummary.priority_notes}</p>
                  </div>
                </div>
              )}

              {/* Ticket Age & Organization */}
              <div className="flex items-center gap-4 text-xs text-[#59554D] pt-2 border-t border-[#DAD5CA]">
                <span>🏢 {handoverSummary.organization}</span>
                <span>📅 {handoverSummary.ticket_age_days} days old</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-[#59554D]">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-[#59554D]" />
              <p>Failed to load handover summary</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSelectedHandover(null); setHandoverSummary(null); }}
              className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
            >
              Close
            </Button>
            {handoverSummary && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShareTicketKey(handoverSummary.ticket_key);
                    setShowShareKnowledge(true);
                    setSelectedHandover(null);
                    setHandoverSummary(null);
                  }}
                  className="border-[#FFF8D6] bg-[#FFF8D6] text-[#C9A400] hover:bg-[#FFF8D6]"
                  data-testid="share-knowledge-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Share Knowledge
                </Button>
                <a
                  href={`${JIRA_URL}/${handoverSummary.ticket_key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in JIRA
                  </Button>
                </a>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Learning & Feedback Dialog */}
      <Dialog open={showFeedbackPanel} onOpenChange={setShowFeedbackPanel}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="feedback-description">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#0E6B3F]" />
              Learning & Performance Feedback
            </DialogTitle>
            <p id="feedback-description" className="sr-only">Your performance feedback based on analyzed call recordings and ticket comments</p>
          </DialogHeader>
          
          {loadingFeedback ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0E6B3F] mb-4" />
              <p className="text-[#59554D]">Analyzing your calls and ticket comments...</p>
            </div>
          ) : agentFeedback ? (
            <div className="space-y-4">
              {/* Overview Stats - Show call stats OR comment stats based on what's available */}
              {agentFeedback.total_analyzed_calls > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-[#E3F3E9] to-[#E3F3E9] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#0E6B3F]">
                      {agentFeedback.average_sentiment_score?.toFixed(1) || "—"}
                    </p>
                    <p className="text-xs text-[#59554D]">Avg Sentiment Score</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-[#FFF8D6] to-[#FFF8D6] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#8A6D00]">
                      {agentFeedback.average_agent_score?.toFixed(1) || "—"}
                    </p>
                    <p className="text-xs text-[#59554D]">Avg Performance Rating</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-[#EFF6FF] to-[#EFF6FF] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#1D4ED8]">
                      {agentFeedback.total_analyzed_calls}
                    </p>
                    <p className="text-xs text-[#59554D]">Calls Analyzed</p>
                  </div>
                </div>
              ) : agentFeedback.comment_analysis ? (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-gradient-to-br from-[#E3F3E9] to-[#E3F3E9] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#0E6B3F]">
                      {agentFeedback.comment_analysis.overall_rating || "—"}/5
                    </p>
                    <p className="text-xs text-[#59554D]">Overall Quality</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-[#FFF8D6] to-[#FFF8D6] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#8A6D00]">
                      {agentFeedback.comment_analysis.tone_rating || "—"}/5
                    </p>
                    <p className="text-xs text-[#59554D]">Tone</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-[#FFF7D1] to-[#FFF7D1] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#59554D]">
                      {agentFeedback.comment_analysis.clarity_rating || "—"}/5
                    </p>
                    <p className="text-xs text-[#59554D]">Clarity</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-[#EFF6FF] to-[#EFF6FF] rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#1D4ED8]">
                      {agentFeedback.total_analyzed_comments || 0}
                    </p>
                    <p className="text-xs text-[#59554D]">Comments Analyzed</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-[#F4F2ED] rounded-lg text-center">
                  <p className="text-sm text-[#8A857A]">No call recordings or comments analyzed yet</p>
                </div>
              )}

              {/* AI Insights */}
              {agentFeedback.ai_insights && (
                <div className="p-4 bg-gradient-to-br from-[#EFF6FF] to-[#EFF6FF] rounded-lg border border-[#EFF6FF]">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-[#1D4ED8] shrink-0" />
                    <div>
                      <p className="text-sm text-[#1A1A1A] font-medium">{agentFeedback.ai_insights.summary}</p>
                      <div className="mt-3 p-2 bg-[#F4F2ED] rounded">
                        <p className="text-xs text-[#59554D] mb-1">Top Tip:</p>
                        <p className="text-sm text-[#8A6D00]">{agentFeedback.ai_insights.top_tip}</p>
                      </div>
                      <p className="text-sm text-[#0E6B3F] mt-3 italic">{agentFeedback.ai_insights.encouragement}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Strengths & Areas for Improvement */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4 text-[#0E6B3F]" />
                    Your Strengths
                  </h4>
                  {agentFeedback.strengths?.length > 0 ? (
                    <ul className="space-y-2">
                      {agentFeedback.strengths.map((strength, idx) => (
                        <li key={idx} className="text-xs text-[#59554D] flex items-start gap-2">
                          <CheckCircle className="w-3 h-3 text-[#0E6B3F] shrink-0 mt-0.5" />
                          {strength}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-[#8A857A]">No strengths identified yet</p>
                  )}
                </div>
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#59554D]" />
                    Areas to Improve
                  </h4>
                  {agentFeedback.improvements?.length > 0 ? (
                    <ul className="space-y-2">
                      {agentFeedback.improvements.map((improvement, idx) => (
                        <li key={idx} className="text-xs text-[#59554D] flex items-start gap-2">
                          <ArrowRight className="w-3 h-3 text-[#59554D] shrink-0 mt-0.5" />
                          {improvement}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-[#8A857A]">No improvements identified yet</p>
                  )}
                </div>
              </div>

              {/* Recent Analyzed Calls */}
              {agentFeedback.recent_calls?.length > 0 && (
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <FileAudio className="w-4 h-4 text-[#8A6D00]" />
                    Recent Analyzed Calls
                  </h4>
                  <div className="space-y-2">
                    {agentFeedback.recent_calls.slice(0, 3).map((call, idx) => (
                      <div key={idx} className="p-2 bg-[#FFFFFF] rounded flex items-center justify-between">
                        <div>
                          <p className="text-xs text-[#1A1A1A] font-mono">{call.filename || `Call ${idx + 1}`}</p>
                          <p className="text-[10px] text-[#59554D]">
                            {call.sentiment_label && <span className="mr-2">Sentiment: {call.sentiment_label}</span>}
                            {call.agent_rating && <span>Rating: {call.agent_rating}</span>}
                          </p>
                        </div>
                        {call.sentiment_score && (
                          <Badge className={
                            call.sentiment_score >= 4 ? "bg-[#E3F3E9] text-[#0E6B3F]" :
                            call.sentiment_score >= 3 ? "bg-[#FFF7D1] text-[#6B5600]" :
                            "bg-[#FFE9E0] text-[#C23A00]"
                          }>
                            {call.sentiment_score}/5
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Written Communication Analysis */}
              {agentFeedback.comment_analysis && (
                <div className="p-4 bg-gradient-to-br from-[#FFF8D6] to-[#EFF6FF] rounded-lg border border-[#FFF8D6]">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#8A6D00]" />
                    Written Communication Analysis
                    <Badge className="bg-[#FFF8D6] text-[#8A6D00] text-[10px]">
                      {agentFeedback.total_analyzed_comments || 0} comments
                    </Badge>
                  </h4>
                  
                  {/* Rating Bars */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#59554D] w-16">Tone</span>
                      <div className="flex-1 h-2 bg-[#DAD5CA] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#FFD108] rounded-full transition-all" 
                          style={{width: `${(agentFeedback.comment_analysis.tone_rating || 0) * 20}%`}}
                        />
                      </div>
                      <span className="text-xs text-[#1A1A1A] w-6">{agentFeedback.comment_analysis.tone_rating}/5</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#59554D] w-16">Clarity</span>
                      <div className="flex-1 h-2 bg-[#DAD5CA] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#0E6B3F] rounded-full transition-all" 
                          style={{width: `${(agentFeedback.comment_analysis.clarity_rating || 0) * 20}%`}}
                        />
                      </div>
                      <span className="text-xs text-[#1A1A1A] w-6">{agentFeedback.comment_analysis.clarity_rating}/5</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#59554D] w-16">Empathy</span>
                      <div className="flex-1 h-2 bg-[#DAD5CA] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#1D4ED8] rounded-full transition-all" 
                          style={{width: `${(agentFeedback.comment_analysis.empathy_rating || 0) * 20}%`}}
                        />
                      </div>
                      <span className="text-xs text-[#1A1A1A] w-6">{agentFeedback.comment_analysis.empathy_rating}/5</span>
                    </div>
                  </div>

                  {/* Writing Strengths & Improvements */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {agentFeedback.comment_analysis.writing_strengths?.length > 0 && (
                      <div className="p-2 bg-[#FFFFFF] rounded">
                        <p className="text-[10px] text-[#0E6B3F] font-medium mb-1">Writing Strengths</p>
                        <ul className="space-y-1">
                          {agentFeedback.comment_analysis.writing_strengths.slice(0, 2).map((s, i) => (
                            <li key={i} className="text-[10px] text-[#59554D] flex items-start gap-1">
                              <CheckCircle className="w-2.5 h-2.5 text-[#0E6B3F] shrink-0 mt-0.5" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {agentFeedback.comment_analysis.writing_improvements?.length > 0 && (
                      <div className="p-2 bg-[#FFFFFF] rounded">
                        <p className="text-[10px] text-[#59554D] font-medium mb-1">Areas to Improve</p>
                        <ul className="space-y-1">
                          {agentFeedback.comment_analysis.writing_improvements.slice(0, 2).map((s, i) => (
                            <li key={i} className="text-[10px] text-[#59554D] flex items-start gap-1">
                              <ArrowRight className="w-2.5 h-2.5 text-[#59554D] shrink-0 mt-0.5" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* AI Tip for Writing */}
                  {agentFeedback.comment_analysis.suggested_improvement && (
                    <div className="p-2 bg-[#F4F2ED] rounded border-l-2 border-[#8A6D00]">
                      <p className="text-[10px] text-[#8A857A] mb-0.5">Writing Tip:</p>
                      <p className="text-xs text-[#59554D]">{agentFeedback.comment_analysis.suggested_improvement}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Recent Comments */}
              {agentFeedback.recent_comments?.length > 0 && (
                <div className="p-4 bg-[#F4F2ED] rounded-lg">
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#1D4ED8]" />
                    Recent Customer Comments
                  </h4>
                  <div className="space-y-2">
                    {agentFeedback.recent_comments.slice(0, 3).map((comment, idx) => (
                      <div key={idx} className="p-2 bg-[#FFFFFF] rounded">
                        <div className="flex items-center justify-between mb-1">
                          <a 
                            href={`${JIRA_URL}/${comment.ticket_key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#8A6D00] hover:underline"
                          >
                            {comment.ticket_key}
                          </a>
                          <span className="text-[10px] text-[#8A857A]">
                            {comment.date ? formatDate(comment.date) : ''}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#59554D] line-clamp-2">{comment.comment_preview}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#59554D]">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-[#59554D]" />
              <p>No feedback data available yet</p>
              <p className="text-xs mt-1">Call recordings need to be transcribed and analyzed first</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFeedbackPanel(false)}
              className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
            >
              Close
            </Button>
            <Button
              onClick={fetchAgentFeedback}
              disabled={loadingFeedback}
              className="bg-[#0E6B3F] hover:bg-[#0B4F2E] text-[#1A1A1A]"
            >
              {loadingFeedback ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Knowledge Dialog */}
      <Dialog open={showShareKnowledge} onOpenChange={setShowShareKnowledge}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" aria-describedby="share-knowledge-description">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#C9A400]" />
              Share Resolution Knowledge
            </DialogTitle>
            <p id="share-knowledge-description" className="sr-only">Share how you resolved this ticket to help other agents</p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#59554D]">Ticket</Label>
              <p className="text-[#1A1A1A] font-mono">{shareTicketKey}</p>
            </div>
            
            <div>
              <Label htmlFor="resolution-summary" className="text-[#59554D]">Resolution Summary *</Label>
              <Textarea
                id="resolution-summary"
                value={shareResolution}
                onChange={(e) => setShareResolution(e.target.value)}
                placeholder="Briefly describe how you resolved this issue..."
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="resolution-steps" className="text-[#59554D]">Step-by-Step Solution (optional)</Label>
              <Textarea
                id="resolution-steps"
                value={shareSteps}
                onChange={(e) => setShareSteps(e.target.value)}
                placeholder="1. First, I did...\n2. Then, I checked...\n3. Finally, I..."
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] mt-1"
                rows={4}
              />
            </div>
            
            <div className="p-3 bg-[#FFF8D6] rounded-lg border border-[#FFF8D6]">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
                <p className="text-xs text-[#59554D]">
                  AI will enhance your resolution into a structured knowledge article that other agents can find when handling similar issues.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowShareKnowledge(false); setShareTicketKey(null); setShareResolution(""); setShareSteps(""); }}
              className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleShareKnowledge}
              disabled={sharingKnowledge || !shareResolution.trim()}
              className="bg-[#C9A400] hover:bg-[#B08F00] text-[#1A1A1A]"
            >
              {sharingKnowledge ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BookOpen className="w-4 h-4 mr-2" />}
              Share Knowledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={(open) => {
        if (!open) setTicketAttachments([]);
        setShowCreateTicket(open);
      }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="create-ticket-description" data-agent-theme={agentTheme}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Support Ticket</DialogTitle>
            <p id="create-ticket-description" className="sr-only">Fill in the form to create a new JIRA support ticket</p>
          </DialogHeader>
          
          {selectedCall && (
            <div className="bg-[#F4F2ED] rounded-lg p-3 mb-4">
              <p className="text-xs text-[#59554D] mb-1">From Call</p>
              <div className="flex items-center gap-4">
                <Phone className="w-4 h-4 text-[#8A6D00]" />
                <span className="font-mono">{selectedCall.origin || selectedCall.destination}</span>
                <span className="text-[#59554D]">•</span>
                <span className="text-[#59554D]">{selectedCall.datetime?.split(' ')[1]}</span>
                <span className="text-[#59554D]">•</span>
                <span className="text-[#59554D]">{selectedCall.duration_hr || selectedCall.duration + 's'}</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Product — required first field (directive §6). Everything below
                depends on it; Core/Guardian arrive next so they are shown but
                not yet selectable. */}
            <div className="space-y-2">
              <Label className="text-[#59554D]">Product <span className="text-[#C23A00]">*</span></Label>
              <Select
                value={ticketForm.product}
                onValueChange={(v) => setTicketForm({ ...ticketForm, product: v })}
              >
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="ticket-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <SelectItem value="Allocate + Operator">Allocate + Operator</SelectItem>
                  <SelectItem value="Core" disabled>Core (coming soon)</SelectItem>
                  <SelectItem value="Guardian" disabled>Guardian (coming soon)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8A857A]" data-testid="ticket-product-landing">
                Creates a Jira Service Desk ticket.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#59554D]">Request Type</Label>
                <Select 
                  value={ticketForm.requestTypeId} 
                  onValueChange={(v) => setTicketForm({...ticketForm, requestTypeId: v})}
                >
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="ticket-request-type-select">
                    <SelectValue placeholder="Select request type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-h-[240px]">
                    {requestTypes.map((rt) => (
                      <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#59554D]">Priority</Label>
                <Select 
                  value={ticketForm.priority} 
                  onValueChange={(v) => setTicketForm({...ticketForm, priority: v})}
                >
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectItem value="1">Highest</SelectItem>
                    <SelectItem value="2">High</SelectItem>
                    <SelectItem value="3">Medium</SelectItem>
                    <SelectItem value="4">Low</SelectItem>
                    <SelectItem value="5">Lowest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Organization and Reporter Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#59554D] flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Organisation
                </Label>
                <Select 
                  value={ticketForm.organizationId} 
                  onValueChange={(v) => {
                    const orgValue = v === "none" ? "" : v;
                    setTicketForm({...ticketForm, organizationId: orgValue, reporterEmail: ""});
                    fetchOrganizationContacts(orgValue);
                  }}
                  disabled={loadingOrgs}
                >
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectValue placeholder={loadingOrgs ? "Loading..." : "Select organisation"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-h-[200px]">
                    <SelectItem value="none">No organisation</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id || org.name} value={org.name}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#59554D] flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Reporter Email
                </Label>
                <Select 
                  value={ticketForm.reporterEmail} 
                  onValueChange={(v) => setTicketForm({...ticketForm, reporterEmail: v})}
                  disabled={!ticketForm.organizationId || organizationContacts.length === 0}
                >
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectValue placeholder={
                      !ticketForm.organizationId ? "Select org first" :
                      organizationContacts.length === 0 ? "No contacts found" :
                      "Select reporter"
                    } />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-h-[200px]">
                    {organizationContacts.map((contact) => (
                      <SelectItem key={contact.email || contact.accountId} value={contact.email || contact.accountId}>
                        {contact.displayName || contact.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#59554D]">Summary</Label>
              <Input
                value={ticketForm.summary}
                onChange={(e) => handleTicketTitleChange(e.target.value)}
                placeholder="Brief description of the issue"
                className="bg-[#F4F2ED] border-[#DAD5CA]"
                data-testid="ticket-summary"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#59554D]">Description</Label>
              <Textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                placeholder="Detailed description..."
                rows={6}
                className="bg-[#F4F2ED] border-[#DAD5CA]"
                data-testid="ticket-description"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-[#59554D] flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments (optional)
              </Label>
              <div className="bg-[#F4F2ED] border border-dashed border-[#DAD5CA] rounded-lg p-3">
                <input
                  type="file"
                  id="ticket-attachments-input"
                  multiple
                  onChange={(e) => {
                    const incoming = Array.from(e.target.files || []);
                    if (incoming.length === 0) return;
                    // 25MB per file
                    const tooBig = incoming.filter(f => f.size > 25 * 1024 * 1024);
                    if (tooBig.length > 0) {
                      toast.error(`Skipped: ${tooBig.map(f => f.name).join(", ")} — each file must be under 25MB`);
                    }
                    const accepted = incoming.filter(f => f.size <= 25 * 1024 * 1024);
                    // De-dupe by name + size
                    setTicketAttachments(prev => {
                      const seen = new Set(prev.map(f => `${f.name}|${f.size}`));
                      const merged = [...prev];
                      accepted.forEach(f => {
                        const k = `${f.name}|${f.size}`;
                        if (!seen.has(k)) { merged.push(f); seen.add(k); }
                      });
                      return merged;
                    });
                    e.target.value = "";
                  }}
                  className="hidden"
                  data-testid="ticket-attachments-input"
                />
                <label
                  htmlFor="ticket-attachments-input"
                  className="flex items-center gap-2 cursor-pointer text-sm text-[#8A6D00] hover:text-[#8A6D00]"
                  data-testid="ticket-attachments-trigger"
                >
                  <Plus className="w-4 h-4" />
                  Add files or images
                  <span className="text-xs text-[#8A857A] ml-auto">Max 25MB each</span>
                </label>
                {ticketAttachments.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {ticketAttachments.map((f, idx) => {
                      const sizeKB = f.size / 1024;
                      const sizeLabel = sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB.toFixed(0)} KB`;
                      const isImg = f.type.startsWith("image/");
                      return (
                        <div
                          key={`${f.name}-${idx}`}
                          className="flex items-center gap-2 bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5"
                          data-testid={`ticket-attachment-${idx}`}
                        >
                          {isImg ? (
                            <ImageIcon className="w-4 h-4 text-[#1D4ED8]" />
                          ) : (
                            <FileText className="w-4 h-4 text-[#8A6D00]" />
                          )}
                          <span className="text-sm flex-1 truncate" title={f.name}>{f.name}</span>
                          <span className="text-xs text-[#8A857A]">{sizeLabel}</span>
                          <button
                            type="button"
                            onClick={() => setTicketAttachments(prev => prev.filter((_, i) => i !== idx))}
                            className="text-[#C23A00] hover:text-[#FFE9E0] p-0.5 rounded"
                            data-testid={`ticket-attachment-remove-${idx}`}
                            title="Remove"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Smart Suggestions Panel */}
            {(loadingSuggestions || suggestions) && (
              <div className="mt-4 p-4 bg-gradient-to-br from-[#FFF8D6] to-[#EFF6FF] rounded-lg border border-[#FFF8D6]">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4 text-[#C9A400]" />
                  <span className="text-sm font-medium text-[#59554D]">SMART SUGGESTIONS</span>
                  {loadingSuggestions && <Loader2 className="w-3 h-3 text-[#8A6D00] animate-spin" />}
                </div>
                
                {suggestions && (
                  <div className="space-y-3">
                    {/* Suggested Priority */}
                    {suggestions.suggested_priority && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#59554D]">Suggested Priority:</span>
                        <Badge 
                          className="cursor-pointer hover:opacity-80 bg-[#FFF7D1] text-[#6B5600]"
                          onClick={() => {
                            const priorityMap = { "Highest": "1", "High": "2", "Medium": "3", "Low": "4", "Lowest": "5" };
                            const val = priorityMap[suggestions.suggested_priority] || "3";
                            setTicketForm(prev => ({ ...prev, priority: val }));
                            toast.success(`Priority set to ${suggestions.suggested_priority}`);
                          }}
                        >
                          {suggestions.suggested_priority} (click to apply)
                        </Badge>
                      </div>
                    )}

                    {/* Similar Tickets */}
                    {suggestions.similar_tickets?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <Ticket className="w-3 h-3" /> Similar Tickets:
                        </p>
                        <div className="space-y-1">
                          {suggestions.similar_tickets.slice(0, 3).map((ticket, idx) => (
                            <a
                              key={idx}
                              href={`https://equiprsoftware.atlassian.net/browse/${ticket.ticket_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block p-2 bg-[#F4F2ED] rounded text-xs hover:bg-[#DAD5CA] transition-colors"
                            >
                              <span className="text-[#8A6D00]">{ticket.ticket_key}</span>
                              <span className="text-[#1A1A1A] ml-2">{ticket.summary}</span>
                              <span className="text-[#59554D] ml-2">({ticket.status})</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related Call Transcripts */}
                    {suggestions.related_transcripts?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <FileAudio className="w-3 h-3" /> Related Calls:
                        </p>
                        <div className="space-y-1">
                          {suggestions.related_transcripts.map((tr, idx) => (
                            <div key={idx} className="p-2 bg-[#F4F2ED] rounded text-xs">
                              <span className="text-[#0E6B3F]">{tr.customer_name || 'Customer'}</span>
                              <span className="text-[#59554D] ml-2">({tr.sentiment || 'N/A'})</span>
                              <p className="text-[#8A857A] mt-1 truncate">{tr.transcription_preview}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Knowledge Articles */}
                    {suggestions.knowledge_articles?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> Knowledge Articles:
                        </p>
                        <div className="space-y-1">
                          {suggestions.knowledge_articles.map((article, idx) => (
                            <a
                              key={idx}
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block p-2 bg-[#F4F2ED] rounded text-xs hover:bg-[#DAD5CA] transition-colors"
                            >
                              <span className="text-[#8A6D00]">{article.title}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateTicket(false)}
              className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={creatingTicket || !ticketForm.summary.trim()}
              className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]"
              data-testid="submit-ticket"
            >
              {creatingTicket ? "Creating..." : "Create Ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Resolution Suggestions Dialog */}
      <Dialog open={!!selectedTicketForSuggestions} onOpenChange={(open) => { 
        if (!open) { 
          setSelectedTicketForSuggestions(null); 
          setAiResolutionSuggestions(null); 
        } 
      }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="ai-resolution-description">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#1D4ED8]" />
              Triage
              {selectedTicketForSuggestions && (
                <Badge className="bg-[#FFF8D6] text-[#8A6D00] ml-2">
                  {selectedTicketForSuggestions}
                </Badge>
              )}
            </DialogTitle>
            <p id="ai-resolution-description" className="sr-only">Similar tickets and a grounded suggestion, read live from Atlassian</p>
          </DialogHeader>
          
          <TriagePanel ticketKey={selectedTicketForSuggestions} jiraUrl={JIRA_URL} />

          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedTicketForSuggestions(null);
                setAiResolutionSuggestions(null);
              }}
              className="text-[#59554D] hover:text-[#1A1A1A]"
            >
              Close
            </Button>
            {selectedTicketForSuggestions && (
              <a
                href={`${JIRA_URL}/${selectedTicketForSuggestions}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-9 px-4 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] rounded-md text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in JIRA
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Assistant Sheet (Drawer from right) */}
      <Sheet open={showAIAssistant} onOpenChange={setShowAIAssistant}>
        <SheetContent className="bg-[#FFFFFF] border-l border-[#DAD5CA] text-[#1A1A1A] w-[450px] sm:w-[500px] flex flex-col p-0">
          <SheetHeader className="p-6 pb-4 border-b border-[#DAD5CA]">
            <SheetTitle className="text-xl font-bold flex items-center gap-2 text-[#1A1A1A]">
              <Sparkles className="w-5 h-5 text-[#8A6D00]" />
              AI Support Assistant
            </SheetTitle>
            <p className="text-sm text-[#59554D]">
              Ask me anything about support issues, past tickets, or troubleshooting
            </p>
          </SheetHeader>

          {/* Chat History */}
          <ScrollArea className="flex-1 px-6 py-4">
            <div className="space-y-4">
              {aiHistory.length === 0 && (
                <div className="text-center py-8 text-[#59554D]">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Ask me about:</p>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>• How to resolve common issues</li>
                    <li>• Similar past tickets</li>
                    <li>• Troubleshooting steps</li>
                    <li>• Best practices</li>
                  </ul>
                </div>
              )}
              
              {aiHistory.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[90%] rounded-lg p-3 ${
                    msg.type === 'user' 
                      ? 'bg-[#FFD108] text-[#1A1A1A]' 
                      : msg.error 
                        ? 'bg-[#FFE9E0] text-[#C23A00]'
                        : 'bg-[#F4F2ED] text-[#1A1A1A]'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    
                    {/* Similar Tickets */}
                    {msg.tickets && msg.tickets.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <Ticket className="w-3 h-3" /> Related Tickets:
                        </p>
                        <div className="space-y-1">
                          {msg.tickets.slice(0, 3).map((t, tIdx) => (
                            <a
                              key={tIdx}
                              href={`${JIRA_URL}/${t.ticket_key || t.key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs text-[#8A6D00] hover:underline"
                            >
                              {t.ticket_key || t.key}: {(t.summary || '').slice(0, 50)}...
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related Call Transcripts */}
                    {msg.transcripts && msg.transcripts.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <FileAudio className="w-3 h-3" /> Related Calls:
                        </p>
                        <div className="space-y-1">
                          {msg.transcripts.slice(0, 2).map((tr, trIdx) => (
                            <div key={trIdx} className="text-xs p-2 bg-[#FFFFFF] rounded">
                              <span className="text-[#0E6B3F]">{tr.customer_name || 'Customer'}</span>
                              {tr.sentiment && (
                                <Badge className={`ml-2 text-[10px] ${
                                  tr.sentiment === 'positive' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                  tr.sentiment === 'negative' ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                  'bg-[#FFF7D1] text-[#6B5600]'
                                }`}>
                                  {tr.sentiment}
                                </Badge>
                              )}
                              <p className="text-[#8A857A] mt-1 text-[10px]">{(tr.transcription_preview || '').slice(0, 100)}...</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Knowledge Articles */}
                    {msg.articles && msg.articles.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                        <p className="text-xs text-[#59554D] mb-2 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> Knowledge Base:
                        </p>
                        <div className="space-y-1">
                          {msg.articles.slice(0, 2).map((article, aIdx) => (
                            <a
                              key={aIdx}
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs text-[#8A6D00] hover:underline"
                            >
                              📄 {article.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#F4F2ED] rounded-lg p-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#8A6D00]" />
                    <span className="text-sm text-[#59554D]">Thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="flex gap-2 p-6 pt-4 border-t border-[#DAD5CA]">
            <Input
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && askAI()}
              placeholder="Ask a question..."
              className="flex-1 bg-[#F4F2ED] border-[#DAD5CA]"
              disabled={aiLoading}
              data-testid="ai-question-input"
            />
            <Button
              onClick={askAI}
              disabled={aiLoading || !aiQuestion.trim()}
              className="bg-[#FFD108] hover:bg-[#E0B800]"
              data-testid="ai-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* SQL Copilot Sheet */}
      <Sheet open={showSqlCopilot} onOpenChange={setShowSqlCopilot}>
        <SheetContent className="w-[600px] sm:max-w-[600px] bg-[#FFFFFF] border-l border-[#DAD5CA] text-[#1A1A1A] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <Sparkles className="w-5 h-5 text-[#1D4ED8]" />
              SQL Copilot
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            {/* Database Selector */}
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Database</label>
              <select
                value={sqlClientId}
                onChange={(e) => setSqlClientId(e.target.value)}
                className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
              >
                {availableSchemas.map((schema) => (
                  <option key={schema.client_id} value={schema.client_id}>
                    {schema.client_id} - {schema.database_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Question Input */}
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Ask a question about Allocate data:</label>
              <textarea
                value={sqlQuestion}
                onChange={(e) => setSqlQuestion(e.target.value)}
                placeholder="e.g., Show me data for docket 123..."
                className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] min-h-[80px] resize-none"
                data-testid="sql-copilot-question"
              />
            </div>
            
            {/* Optional Context */}
            <div className="space-y-2">
              <label className="text-sm text-[#59554D]">Optional Context:</label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={sqlContext.docket_number}
                  onChange={(e) => setSqlContext({...sqlContext, docket_number: e.target.value})}
                  placeholder="Docket #"
                  className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] text-sm"
                />
                <input
                  type="text"
                  value={sqlContext.customer}
                  onChange={(e) => setSqlContext({...sqlContext, customer: e.target.value})}
                  placeholder="Customer"
                  className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] text-sm"
                />
                <input
                  type="text"
                  value={sqlContext.booking_id}
                  onChange={(e) => setSqlContext({...sqlContext, booking_id: e.target.value})}
                  placeholder="Booking ID"
                  className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] text-sm"
                />
              </div>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={generateSqlQuery}
              disabled={generatingSql || !sqlQuestion.trim()}
              className="w-full bg-[#1D4ED8] hover:bg-[#1D4ED8] text-[#1A1A1A]"
              data-testid="sql-copilot-generate-btn"
            >
              {generatingSql ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate SQL
                </>
              )}
            </Button>
            
            {/* Results */}
            {sqlResult && (
              <div className="space-y-4 pt-4 border-t border-[#DAD5CA]">
                {/* Confidence Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#59554D]">Confidence:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    sqlResult.confidence === 'High' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                    sqlResult.confidence === 'Medium' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                    'bg-[#FFE9E0] text-[#C23A00]'
                  }`}>
                    {sqlResult.confidence}
                  </span>
                  {sqlResult.tables_used?.length > 0 && (
                    <span className="text-xs text-[#8A857A]">
                      Tables: {sqlResult.tables_used.join(', ')}
                    </span>
                  )}
                </div>
                
                {/* SQL Output */}
                {sqlResult.sql ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-[#59554D]">Generated SQL:</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copySqlToClipboard}
                        className="text-[#8A6D00] hover:text-[#8A6D00] h-7"
                        data-testid="sql-copilot-copy-btn"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <pre className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-3 text-sm text-[#59554D] overflow-x-auto whitespace-pre-wrap font-mono">
                      {sqlResult.sql}
                    </pre>
                  </div>
                ) : (
                  <div className="bg-[#FFE9E0] border border-[#FFE9E0] rounded-lg p-3 text-sm text-[#C23A00]">
                    {sqlResult.error || "Failed to generate SQL"}
                  </div>
                )}
                
                {/* Explanation */}
                {sqlResult.explanation && (
                  <div>
                    <label className="text-sm text-[#59554D] mb-1 block">Explanation:</label>
                    <p className="text-sm text-[#59554D] bg-[#F4F2ED] rounded-lg p-3">
                      {sqlResult.explanation}
                    </p>
                  </div>
                )}
                
                {/* Warnings */}
                {sqlResult.warnings?.length > 0 && (
                  <div className="bg-[#FFF7D1] border border-[#FFF7D1] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-[#59554D]" />
                      <span className="text-sm font-medium text-[#59554D]">Warnings:</span>
                    </div>
                    <ul className="text-xs text-[#FFF7D1] list-disc list-inside">
                      {sqlResult.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Feedback Buttons */}
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-sm text-[#59554D]">Was this helpful?</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitSqlFeedback('helpful')}
                    className="border-[#0E6B3F] text-[#0E6B3F] hover:bg-[#E3F3E9] h-8"
                    data-testid="sql-feedback-helpful"
                  >
                    <ThumbsUp className="w-3 h-3 mr-1" />
                    Helpful
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitSqlFeedback('not_helpful')}
                    className="border-[#C23A00] text-[#C23A00] hover:bg-[#FFE9E0] h-8"
                    data-testid="sql-feedback-not-helpful"
                  >
                    <ThumbsDown className="w-3 h-3 mr-1" />
                    Not Helpful
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitSqlFeedback('needs_review')}
                    className="border-[#E0B800] text-[#59554D] hover:bg-[#FFF7D1] h-8"
                    data-testid="sql-feedback-needs-review"
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Needs Review
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/**
 * Compact list of tickets the current agent has escalated. Sourced from
 * the server-side `escalations` collection so the list survives status
 * changes — an escalated ticket that's now Resolved still shows up here
 * as a historical record of "I escalated this".
 */
function EscalationsPanel({ escalations, loading, onOpenRunbook, jiraUrl }) {
  if (loading) {
    return (
      <div className="space-y-2 pr-2" data-testid="escalations-loading">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[72px] rounded-lg bg-[#F6F4EF] border border-[#EFECE4] animate-pulse"
            style={{ animationDelay: `${i * 70}ms` }}
          />
        ))}
      </div>
    );
  }
  if (!escalations || escalations.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-[#59554D] opacity-60" />
        <p className="text-[#59554D] text-sm">No escalations yet.</p>
        <p className="text-xs text-[#8A857A] mt-1">
          When you escalate a ticket, it&apos;ll show up here for follow-up.
        </p>
      </div>
    );
  }

  // Newest first. Backend already sorts but defend against shape changes.
  const sorted = [...escalations].sort((a, b) =>
    (b.escalated_at || "").localeCompare(a.escalated_at || "")
  );

  return (
    <div className="space-y-2 pr-2" data-testid="escalations-list">
      <p className="text-xs text-[#8A857A] mb-2">
        {sorted.length} ticket{sorted.length === 1 ? "" : "s"} escalated in the last 12 months
      </p>
      {sorted.map((e, idx) => (
        <div
          key={`${e.ticket_key}-${e.escalated_at || idx}`}
          className="p-3 rounded-lg bg-[#FFFFFF] border border-[#DAD5CA] hover:border-[#FFE9E0] transition-colors"
          data-testid={`escalation-row-${e.ticket_key}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <a
                  href={`${jiraUrl}/${e.ticket_key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#8A6D00] hover:underline font-mono text-xs"
                >
                  {e.ticket_key}
                </a>
                {e.priority && (
                  <Badge className="bg-[#DAD5CA] text-[#59554D] text-xs px-1.5 py-0">
                    {e.priority}
                  </Badge>
                )}
                <span className="text-[10px] bg-[#FFE9E0] text-[#C23A00] border border-[#FFE9E0] px-1.5 py-0.5 rounded">
                  → {e.escalated_to || "Senior support"}
                </span>
              </div>
              <p className="text-sm text-[#1A1A1A] truncate">
                {e.summary || "(no summary captured)"}
              </p>
              <div className="flex items-center gap-3 text-xs text-[#8A857A] mt-1 flex-wrap">
                {e.organization && (
                  <button
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); onOpenRunbook(e.organization); }}
                    className="hover:text-[#8A6D00] hover:underline"
                    title="Open client runbook"
                    data-testid={`escalation-runbook-link-${e.ticket_key}`}
                  >
                    {e.organization}
                  </button>
                )}
                {e.escalated_at && (
                  <span>
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatDateTime(e.escalated_at)}
                  </span>
                )}
              </div>
              {e.reason && (
                <p className="text-xs text-[#59554D] mt-2 italic bg-[#FFFFFF] rounded p-2 border border-[#DAD5CA]">
                  &quot;{e.reason}&quot;
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


/**
 * "My Team" panel for team_leads (Kevin Carbe). Surfaces escalation inbox,
 * direct-report workload, cross-team SLA risk, recent activity, and 7-day
 * per-member stats. All data comes from a single /api/team/overview call.
 */
/* Module-scope helpers/components for MyTeamPanel (kept out of render to avoid
   remounting on every render and to satisfy react-hooks purity rules). */
const fmtTeamTime = (iso) => (iso ? formatDateTime(iso) : "—");
const relTeamTime = (iso) => {
  if (!iso) return "";
  const diffMin = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diffMin < 60) return `${Math.round(diffMin)}m ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
  return `${Math.round(diffMin / 1440)}d ago`;
};

const TeamSection = ({ icon: Icon, title, accent, count, children, actions }) => (
  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
    <CardHeader className="pb-3 flex flex-row items-center justify-between">
      <CardTitle className="text-base text-[#1A1A1A] flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        {title}
        {count != null && (
          <span className="text-xs bg-[#DAD5CA] text-[#59554D] px-1.5 py-0.5 rounded-full ml-1">
            {count}
          </span>
        )}
      </CardTitle>
      {actions}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

function MyTeamPanel({ overview, loading, onRefresh, onOpenRunbook, jiraUrl }) {
  // Hooks must be declared before any early return (rules-of-hooks).
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyInternal, setReplyInternal] = useState(false);
  const [replyBusy, setReplyBusy] = useState(false);
  const [resolvingKey, setResolvingKey] = useState(null);
  const [confirmDeEscalate, setConfirmDeEscalate] = useState(null); // ticket_key pending confirmation

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center py-16 text-[#59554D]">
        <div className="animate-spin w-6 h-6 border-2 border-[#E0B800] border-t-transparent rounded-full mr-3" />
        Loading team overview…
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="text-center py-16 text-[#59554D]">
        <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No team data available</p>
        <Button onClick={onRefresh} variant="outline" className="mt-4 border-[#DAD5CA] bg-transparent">
          Retry
        </Button>
      </div>
    );
  }

  const breachedTotal = (overview.workload || []).reduce((s, w) => s + w.sla_breached, 0);
  const atRiskTotal = overview.sla_risk?.length || 0;

  // ── Escalation review actions (#12): direct reply + move out of Escalated ──
  const handleEscalationReply = async (ticketKey) => {
    if (!replyText.trim()) {
      toast.error("Enter a message first");
      return;
    }
    setReplyBusy(true);
    try {
      await axios.post(`${API}/tickets/${ticketKey}/escalation/reply`, {
        reviewer_name: overview.lead_name || "Team Lead",
        message: replyText.trim(),
        internal: replyInternal,
      });
      toast.success(replyInternal ? `Internal note added to ${ticketKey}` : `Reply sent to customer on ${ticketKey}`);
      setReplyingTo(null);
      setReplyText("");
      setReplyInternal(false);
    } catch (err) {
      console.error("Escalation reply failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to send reply");
    } finally {
      setReplyBusy(false);
    }
  };

  const handleDeEscalate = async (ticketKey) => {
    setResolvingKey(ticketKey);
    try {
      const res = await axios.post(`${API}/tickets/${ticketKey}/de-escalate`, {
        reviewer_name: overview.lead_name || "Team Lead",
      });
      if (res.data.success) {
        toast.success(`${ticketKey} moved to ${res.data.new_status} — cleared from Escalated`);
      } else {
        toast.warning(res.data.error || `${ticketKey} marked reviewed (update status in JIRA manually)`);
      }
      onRefresh();
    } catch (err) {
      console.error("De-escalate failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to update ticket");
    } finally {
      setResolvingKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-team-panel">
      {/* De-escalate confirm (§ directive item 11) */}
      <Dialog open={!!confirmDeEscalate} onOpenChange={(open) => { if (!open) setConfirmDeEscalate(null); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="deescalate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Mark {confirmDeEscalate} reviewed?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#59554D] py-1">
            This moves the ticket out of the Escalated queue and back to an active support status in JIRA. The escalation record is cleared and the assignee becomes responsible again.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeEscalate(null)}
              className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"
              data-testid="deescalate-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { const k = confirmDeEscalate; setConfirmDeEscalate(null); handleDeEscalate(k); }}
              className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-medium"
              data-testid="deescalate-confirm-confirm"
            >
              Mark reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A1A1A]">
            My Team
            <span className="text-sm font-normal text-[#59554D] ml-2">
              · {overview.members.length} direct report{overview.members.length === 1 ? "" : "s"}
              {overview.lead_name && ` · led by ${overview.lead_name}`}
            </span>
          </h2>
          <p className="text-xs text-[#8A857A] mt-0.5">
            Last refreshed {fmtTeamTime(overview.generated_at)}
          </p>
        </div>
        <Button
          onClick={onRefresh}
          disabled={loading}
          size="sm"
          variant="outline"
          className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
          data-testid="team-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-3 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-[#C23A00]" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{overview.inbox.length}</p>
            <p className="text-xs text-[#59554D]">Escalations to me ({overview.window_days}d)</p>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-3 text-center">
            <Ticket className="w-5 h-5 mx-auto mb-1 text-[#8A6D00]" />
            <p className="text-2xl font-bold text-[#1A1A1A]">
              {(overview.workload || []).reduce((s, w) => s + w.open_count, 0)}
            </p>
            <p className="text-xs text-[#59554D]">Team open tickets</p>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-3 text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-[#59554D]" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{atRiskTotal}</p>
            <p className="text-xs text-[#59554D]">At SLA risk</p>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-3 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-[#C23A00]" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{breachedTotal}</p>
            <p className="text-xs text-[#59554D]">SLA breached</p>
          </CardContent>
        </Card>
      </div>

      {/* Escalation Inbox */}
      <TeamSection
        icon={AlertTriangle}
        title="Escalation Inbox"
        accent="#C23A00"
        count={overview.inbox.length}
      >
        {overview.inbox.length === 0 ? (
          <p className="text-sm text-[#8A857A] text-center py-4">
            No escalations directed at you in the last {overview.window_days} days.
          </p>
        ) : (
          <div className="space-y-2" data-testid="team-inbox-list">
            {overview.inbox.map((e, idx) => (
              <div
                key={`${e.ticket_key}-${e.escalated_at || idx}`}
                className="p-3 rounded-lg bg-[#FFFFFF] border border-[#DAD5CA]"
                data-testid={`inbox-row-${e.ticket_key}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <a href={`${jiraUrl}/${e.ticket_key}`} target="_blank" rel="noopener noreferrer"
                     className="text-[#8A6D00] hover:underline font-mono text-xs">
                    {e.ticket_key}
                  </a>
                  {e.priority && (
                    <Badge className="bg-[#DAD5CA] text-[#59554D] text-xs px-1.5 py-0">{e.priority}</Badge>
                  )}
                  <span className="text-[10px] text-[#8A857A]">
                    from <strong className="text-[#1A1A1A]">{e.escalated_by || "—"}</strong>
                  </span>
                  <span className="text-[10px] text-[#8A857A]">· {relTeamTime(e.escalated_at)}</span>
                </div>
                <p className="text-sm text-[#1A1A1A] truncate">{e.summary || "(no summary)"}</p>
                {e.organization && (
                  <button
                    type="button"
                    onClick={() => onOpenRunbook(e.organization)}
                    className="text-xs text-[#8A857A] hover:text-[#8A6D00] hover:underline mt-1"
                    title="Open client runbook"
                  >
                    {e.organization}
                  </button>
                )}
                {e.reason && (
                  <p className="text-xs text-[#59554D] italic mt-2 bg-[#FFFFFF] rounded p-2 border border-[#DAD5CA]">
                    &quot;{e.reason}&quot;
                  </p>
                )}
                {e.ai_summary && (
                  <div className="text-xs text-[#59554D] mt-2 bg-[#EFF6FF] rounded p-2 border border-[#EFF6FF]">
                    <span className="flex items-center gap-1 text-[#A78BFA] font-medium mb-1">
                      <Sparkles className="w-3 h-3" /> Why it was escalated
                    </span>
                    {e.ai_summary}
                  </div>
                )}
                {/* Manager actions: direct reply + move out of Escalated (#12) */}
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setReplyingTo(replyingTo === e.ticket_key ? null : e.ticket_key); setReplyText(""); setReplyInternal(false); }}
                    className="h-7 px-2 text-xs text-[#8A6D00] hover:text-[#1A1A1A] hover:bg-[#FFF8D6]"
                    data-testid={`inbox-reply-btn-${e.ticket_key}`}
                  >
                    <MessageSquare className="w-3 h-3 mr-1" /> Reply
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDeEscalate(e.ticket_key)}
                    disabled={resolvingKey === e.ticket_key}
                    className="h-7 px-2 text-xs text-[#0E6B3F] hover:text-[#1A1A1A] hover:bg-[#E3F3E9]"
                    data-testid={`inbox-resolve-btn-${e.ticket_key}`}
                    title="Mark reviewed and move ticket out of Escalated"
                  >
                    {resolvingKey === e.ticket_key
                      ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      : <CheckCircle className="w-3 h-3 mr-1" />}
                    Mark reviewed
                  </Button>
                </div>
                {replyingTo === e.ticket_key && (
                  <div className="mt-2 space-y-2 bg-[#FFFFFF] border border-[#DAD5CA] rounded p-2" data-testid={`inbox-reply-box-${e.ticket_key}`}>
                    <Textarea
                      value={replyText}
                      onChange={(ev) => setReplyText(ev.target.value)}
                      placeholder="Type your response…"
                      className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm min-h-[70px]"
                      data-testid={`inbox-reply-input-${e.ticket_key}`}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-[#59554D] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={replyInternal}
                          onChange={(ev) => setReplyInternal(ev.target.checked)}
                          className="w-3.5 h-3.5 accent-[#8A6D00]"
                          data-testid={`inbox-reply-internal-${e.ticket_key}`}
                        />
                        Internal note only (don&apos;t email customer)
                      </label>
                      <Button
                        size="sm"
                        onClick={() => handleEscalationReply(e.ticket_key)}
                        disabled={replyBusy}
                        className="h-7 px-3 text-xs bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]"
                        data-testid={`inbox-reply-send-${e.ticket_key}`}
                      >
                        {replyBusy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </TeamSection>

      {/* Team Workload */}
      <TeamSection icon={Users} title="Team Workload" accent="#8A6D00" count={overview.members.length}>
        <div className="space-y-2" data-testid="team-workload-list">
          {overview.workload.map((w) => {
            const cap = Math.round((w.support_weight || 0) * 100);
            return (
              <div
                key={w.agent_id}
                className="grid grid-cols-12 items-center gap-2 bg-[#FFFFFF] rounded-lg p-3 border border-[#DAD5CA]"
                data-testid={`workload-row-${w.agent_id}`}
              >
                <div className="col-span-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#FFF8D6] flex items-center justify-center text-[#8A6D00] text-xs font-medium">
                    {w.agent_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{w.agent_name}</p>
                    <p className="text-[10px] text-[#8A857A]">{cap}% capacity</p>
                  </div>
                </div>
                <div className="col-span-9 grid grid-cols-5 gap-2 text-xs">
                  <Stat label="Open" value={w.open_count} />
                  <Stat label="High Pri" value={w.high_priority} colorHigh="#6B5600" />
                  <Stat label="SLA Risk" value={w.sla_risk} colorHigh="#6B5600" />
                  <Stat label="Breached" value={w.sla_breached} colorHigh="#C23A00" />
                  <Stat label="Awaiting 1st" value={w.awaiting_first_response} />
                </div>
              </div>
            );
          })}
        </div>
      </TeamSection>

      {/* SLA Risk feed */}
      <TeamSection icon={Clock} title="Team SLA Risk" accent="#6B5600" count={atRiskTotal}>
        {atRiskTotal === 0 ? (
          <p className="text-sm text-[#8A857A] text-center py-4">No SLA-risk tickets across the team.</p>
        ) : (
          <div className="space-y-1.5" data-testid="team-sla-list">
            {overview.sla_risk.map((t) => (
              <div
                key={t.key}
                className="flex items-center gap-3 bg-[#FFFFFF] rounded p-2 border border-[#DAD5CA]"
                data-testid={`sla-row-${t.key}`}
              >
                <a
                  href={`${jiraUrl}/${t.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#8A6D00] hover:underline font-mono text-xs shrink-0"
                >
                  {t.key}
                </a>
                <span className="text-sm text-[#1A1A1A] flex-1 truncate">{t.summary}</span>
                <span className="text-xs text-[#59554D] shrink-0">{t.assignee}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                  t.breached
                    ? "bg-[#FFE9E0] text-[#C23A00] border border-[#FFE9E0]"
                    : "bg-[#FFF7D1] text-[#6B5600] border border-[#FFF7D1]"
                }`}>
                  {t.breached ? "Breached" : "At Risk"} · {t.idle_hours}h
                </span>
              </div>
            ))}
          </div>
        )}
      </TeamSection>

      {/* Activity Feed */}
      <TeamSection icon={Clock} title="Recent Activity (24h)" accent="#0E6B3F" count={overview.activity.length}>
        {overview.activity.length === 0 ? (
          <p className="text-sm text-[#8A857A] text-center py-4">No recent team activity.</p>
        ) : (
          <div className="space-y-1" data-testid="team-activity-list">
            {overview.activity.map((a, idx) => (
              <div
                key={`${a.ticket_key}-${a.kind}-${idx}`}
                className="flex items-start gap-2 text-xs py-1.5 px-2 hover:bg-[#FFFFFF] rounded"
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                  a.kind === "escalation" ? "bg-[#C23A00]" : "bg-[#0E6B3F]"
                }`} />
                <span className="text-[#59554D] flex-1">
                  <strong className="text-[#1A1A1A]">{a.actor}</strong> {a.detail}{" "}
                  <a
                    href={`${jiraUrl}/${a.ticket_key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#8A6D00] hover:underline font-mono"
                  >
                    {a.ticket_key}
                  </a>
                  {a.reason && <span className="text-[#8A857A] italic"> — &quot;{a.reason}&quot;</span>}
                </span>
                <span className="text-[#8A857A] shrink-0">{relTeamTime(a.at)}</span>
              </div>
            ))}
          </div>
        )}
      </TeamSection>

      {/* Per-member stats */}
      <TeamSection icon={Users} title={`Team Stats (${overview.window_days}d)`} accent="#1D4ED8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="team-stats-grid">
          {overview.stats.map((s) => (
            <div
              key={s.agent_id}
              className="bg-[#FFFFFF] rounded-lg p-3 border border-[#DAD5CA]"
              data-testid={`stats-card-${s.agent_id}`}
            >
              <p className="text-sm font-medium text-[#1A1A1A]">{s.agent_name}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div>
                  <p className="text-[10px] text-[#8A857A] uppercase">Resolved</p>
                  <p className="text-lg font-semibold text-[#0E6B3F]">{s.resolved}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#8A857A] uppercase">Escalated</p>
                  <p className="text-lg font-semibold text-[#C23A00]">{s.escalated}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#8A857A] uppercase">Avg 1st Reply</p>
                  <p className="text-lg font-semibold text-[#1A1A1A]">
                    {s.avg_first_response_hours == null ? "—" : `${s.avg_first_response_hours}h`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-[#8A857A] uppercase">Awaiting 1st</p>
                  <p className="text-lg font-semibold text-[#59554D]">{s.awaiting_first_response}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </TeamSection>
    </div>
  );
}

// Small KPI cell used in Team Workload rows
function Stat({ label, value, colorHigh }) {
  const v = value || 0;
  const color = v > 0 && colorHigh ? colorHigh : "#FFFFFF";
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wide text-[#8A857A]">{label}</p>
      <p className="text-lg font-semibold" style={{ color }}>{v}</p>
    </div>
  );
}

